/**
 * Le chef d'orchestre : état, boucle de temps, synchronisation, routage.
 * Chaque écran vit dans `src/ui/vues/`.
 */
import { migrer, type Etat, type Personnage } from "./sim/etat.ts"
import {
  simuler, relancer, crafter, crafterLot, LOT_MAX, garderJet, rangerJet, jeterJet, deposer, equiper,
  vendre, vendreEquipement, broyer, manger, lancerDonjon, repartir, remiseAZeroCarac,
  personnageActif, choisirPersonnage, acheterPersonnage,
  capturer, oublierRecherche, offrirAlmanax, epingler, forgemagie, trouverPorte,
  appliquerCredits, sortirPourVente, vendreLot, broyerLot, vendreRessources, maudireLigne,
} from "./sim/simulate.ts"
import { statsPerso, verdictFamille, xpRequise, palierDe, tempsParKill } from "./sim/combat.ts"
import type { Contenu } from "./sim/contenu.ts"
import { famille, monstre, recolte, item } from "./sim/contenu.ts"
import type { Recette, Slot } from "./sim/objets.ts"
import { craftable, estConsommable } from "./sim/objets.ts"
import { bonusEquipement } from "./sim/objets.ts"
import { CLASSES, classe } from "./sim/classes.ts"
import { nomApparition } from "./sim/collection.ts"
import { niveauMetier, xpMetier, xpRequiseMetier, prixVente } from "./sim/recolte.ts"
import { uuid } from "./net/uuid.ts"
import { idCompte, lireLocal, ecrireLocal, lireDistant, ecrireDistant, arbitrer, prendreLeBail, rendreLeBail } from "./net/save.ts"
import { chargerAtlas, icone, cleClasse, fondMonstre } from "./ui/sprites.ts"
import { consignerEquipe, viderJournal, rendreJournal, rendreJournalEquipe, versionJournal } from "./ui/journal.ts"
import { toast } from "./ui/toast.ts"
import { securiser, connecter, adopter, deconnecter, pseudoLocal } from "./net/compte.ts"
import { esc, nb, vert, orJauge, rougeJauge } from "./ui/fmt.ts"
import { demarrerMesure, mesurer, identifier } from "./mesure.ts"
import type { Issue } from "./sim/forge.ts"
import { vueForge } from "./ui/vues/forge.ts"
import { marcheVide, type EtatMarche } from "./ui/vues/marche.ts"
import {
  etal as etalDistant, credits as creditsDistants, deposer as deposerAnnonce,
  acheter as acheterAnnonce, retirer as retirerAnnonce, acheterPNJ,
} from "./net/hdv.ts"
import type { Ctx } from "./ui/contexte.ts"
import { vueJeu, apercuActivite } from "./ui/vues/jeu.ts"
import { vuePerso, lienPartage } from "./ui/vues/perso.ts"
import { vueMonde, terrainDe, terrainMetier, resultats } from "./ui/vues/monde.ts"
import { vueSac, listeRecettes, contenuCoffre, contenuRessources, piecesSelectionnables, ressourcesVisibles, rendu, etatAffinage, type Tri } from "./ui/vues/sac.ts"
import { vueCollection } from "./ui/vues/collection.ts"
import type { EtatClassement } from "./ui/vues/classement.ts"
import { lireClassement, lireProfil, oublierCache } from "./net/classement.ts"
import { vueChat, panneauChat, type EtatChat } from "./ui/vues/chat.ts"
import {
  barreSocial, vueNews, vueRetours, socialVide, type EtatSocial,
} from "./ui/vues/nouvelles.ts"
import { nouveautes, nonLues as newsNonLues, marquerLues } from "./net/news.ts"
import { dessinerCarte, poserCarte, chiffresPublics, CLE_CARTE, type Chiffres } from "./net/carte.ts"
import { effetsDofus } from "./sim/dofus.ts"
import { obtenus as succesObtenus, points as pointsSucces } from "./sim/succes.ts"
import { retours as lireRetours, envoyerRetour, repondre } from "./net/retours.ts"
import { lus, rafraichir, envoyer, signaler, marquerLu, nonLus } from "./net/chat.ts"

const SYNC_MS = 60_000
const PERIODE_MS = 100
const PERIODE_LOCAL_MS = 2000
const ID = idCompte()

type Onglet = "jeu" | "perso" | "monde" | "sac" | "collec" | "chat"

let contenu: Contenu
let etat: Etat
let version = 0
let statut = "démarrage"
let onglet: Onglet = "jeu"
const sous: Record<Onglet, string> = { jeu: "", perso: "carac", monde: "liste", sac: "equipement", collec: "succes", chat: "" }
let sousZone = "mob"
let tri: Tri = "niveau"
let filtre = "tout"
let filtreCraft = "ok"
let selection: number | null = null
let recrutement = false
let recherche = ""
let pieceChoisie: string | null = null
let dofusChoisi: number | null = null
/** La pièce ouverte à la forge, et ce que le dernier lot a donné. */
let forgeUid: string | null = null
let forgeIssues: Issue[] = []
/** L'Hôtel des Ventes : son étal vient du réseau, il vit donc hors de l'état. */
let marche: EtatMarche = marcheVide()
let sousMarche = "etal"
/**
 * La sélection multiple du sac (GDD §21).
 *
 * Elle vit hors de l'état : c'est une intention en cours, pas une donnée de
 * partie, et elle n'a rien à faire dans une sauvegarde ni chez le serveur.
 */
let lotPieces = new Set<string>()
let lotRessources = new Set<number>()
let enLot = false
let qteVente = 1
let sondeMarche = 0
let filtreEtal = "tout"
let triEtal = "recent"
let nivEtal: [number, number] = [0, 200]
let annonceOuverte: number | null = null
let filtreDonjon = ""
let sertAOuvert: number | null = null
let butinOuvert: string | null = null
let sousSocial = "salon"
let social: EtatSocial = socialVide()
let partage = { envoi: false, message: null as string | null, erreur: false }
/** Les chiffres du miroir, relus au lancement : voir `entretenirCarte`. */
let chiffres: Chiffres | null = null
let pas = "1"
let slotCraft = ""
let caracCraft: string[] = []
let nivCraft: [number, number] = [0, 200]
let affiner = false
let recetteOuverte: number | null = null
let monstreChoisi: number | null = null
let sourcesOuvertes: number | null = null
let panoOuverte: number | null = null
let paliersOuverts: number | null = null
let rechercheSucces = ""
let filtreSucces: "tout" | "restants" | "faits" = "restants"
let categorieSucces: string | null = null
/**
 * Le classement vit **hors de l'état de jeu** : il vient du serveur, il n'a
 * rien à faire dans une sauvegarde et ne doit surtout pas partir sur le réseau
 * à chaque synchronisation.
 */
let classement: EtatClassement = { tri: "points", joueurs: null, erreur: null, ouvert: null, profil: null }
/** Le chat vit lui aussi hors de l'état de jeu : il vient du serveur. */
let chat: EtatChat = { erreur: null, vise: null, ouvert: false, nonLus: 0 }
let sondeChat = 0

const $ = <T extends HTMLElement = HTMLElement>(s: string) => document.querySelector(s) as T
const perso = (): Personnage | undefined => personnageActif(etat)

// ---------------------------------------------------------------- démarrage

async function demarrer() {
  // Avant tout le reste : une page qui plante au chargement doit quand même
  // être comptée, sinon les pannes sont invisibles dans les chiffres.
  demarrerMesure()
  identifier(ID, pseudoLocal())
  const [c] = await Promise.all([
    fetch("/contenu.json").then((r) => r.json() as Promise<Contenu>),
    chargerAtlas(),
  ])
  contenu = c
  peindreIcones()

  const local = lireLocal()
  etat = migrer(local?.etat, Date.now(), (Math.random() * 2 ** 32) >>> 0)
  version = local?.version ?? 0

  try {
    const distant = await lireDistant(ID)
    if (distant) {
      const gagnant = arbitrer(etat, migrer(distant.etat, Date.now(), etat.seed))
      statut = gagnant === distant.etat ? "restauré" : "local conservé"
      etat = gagnant
      version = distant.version
    } else {
      statut = "nouveau compte"
    }
    // On se synchronise **tout de suite**, avant de jouer : c'est ce qui pose le
    // bail d'appareil (GDD §23), et surtout ce qui fait découvrir en trois
    // secondes — et non au bout d'une minute — qu'une autre session tient déjà
    // la partie. Une minute de jeu à jeter, c'est exactement ce qu'on veut éviter.
    await synchroniser()
  } catch {
    statut = "hors ligne"
  }

  // Une vente a pu se conclure pendant qu'on ne jouait pas.
  void encaisserMarche()
  // La carte de partage se fabrique toute seule (GDD §28).
  void entretenirCarte()
  // Les nouveautés sont un fichier statique de quelques kilo-octets : on les
  // charge tout de suite pour que la pastille de la barre soit juste dès la
  // première seconde, sans attendre qu'on ouvre l'écran.
  void nouveautes().then((n) => { social = { ...social, news: n }; majPastilleNews() })
    .catch(() => { /* hors ligne : on réessaiera en ouvrant l'écran */ })

  mesurer("partie chargée", {
    statut,
    personnages: etat.personnages.length,
    niveau: personnageActif(etat)?.niveau ?? 0,
    kamas: etat.kamas,
    securise: Boolean(pseudoLocal()),
  })

  boucle()
  setInterval(synchroniser, SYNC_MS)
  // Le chat se sonde même sans avoir ouvert son onglet : c'est ce qui permet au
  // panneau de grand écran d'annoncer ce qu'on a manqué.
  sondeChat = window.setInterval(() => { void sonderChat() }, 5_000)
  void sonderChat()
  const sauver = () => { ecrireLocal(etat, version); void synchroniser() }
  addEventListener("visibilitychange", () => {
    if (document.hidden) sauver()
    // En revenant sur l'onglet, on se resynchronise aussitôt. C'est le moment
    // où l'on a le plus de chances d'avoir joué ailleurs entre-temps : sans ça,
    // le portable laissé de côté rejouait jusqu'à une minute avant d'apprendre
    // que le téléphone avait pris le relais.
    else void synchroniser()
  })
  // En quittant, on rend le bail : sans ça l'appareil suivant devrait attendre
  // son expiration, et « reprendre sur le téléphone » demanderait deux minutes
  // de patience pour rien.
  addEventListener("pagehide", () => { ecrireLocal(etat, version); rendreLeBail(ID) })
}

/**
 * Ce que la mesure retient de la partie : les franchissements, pas le détail.
 *
 * On ne remonte ni les victoires ni le butin — quelques dizaines par minute et
 * par personnage, sans rien apprendre de plus que la courbe de niveau. Ce qui
 * compte, c'est où les joueurs s'arrêtent : le niveau atteint, le premier boss,
 * et les morts, qui disent quand l'équilibrage décroche.
 */
function suivreProgression() {
  const p = perso()
  if (!p) return
  if (p.niveau > paliersVus.niveau) {
    // Au chargement, on adopte le niveau en cours sans le signaler : ce n'est
    // pas une montée, c'est une reprise.
    if (paliersVus.niveau > 0) mesurer("niveau atteint", { niveau: p.niveau, kamas: etat.kamas })
    paliersVus.niveau = p.niveau
  }
  const boss = etat.bossVaincus.length
  if (paliersVus.boss >= 0 && boss > paliersVus.boss) mesurer("boss vaincu", { total: boss })
  paliersVus.boss = boss

  const morts = etat.personnages.filter((x) => x.arrete).length
  if (paliersVus.morts >= 0 && morts > paliersVus.morts) {
    const tombe = etat.personnages.find((x) => x.arrete)
    const act = tombe?.activite
    mesurer("personnage tombé", {
      niveau: tombe?.niveau,
      activite: act?.type ?? "aucune",
      ou: act?.type === "combat" ? famille(contenu, act.familleId)?.nom
        : act?.type === "donjon" ? contenu.donjons[String(act.donjonId)]?.nom : null,
      vivres: Object.keys(etat.inventaire).length,
    })
  }
  paliersVus.morts = morts
}

let dernierLocal = 0
/**
 * Le dernier bilan de descente déjà montré.
 *
 * Le compte rendu vit dans l'écran Monde, mais une descente s'achève pendant
 * qu'on regarde l'arène : on retombait donc sur un personnage au repos, sans
 * rien apprendre de ce qui venait de se passer, et il fallait deviner qu'il
 * fallait aller voir ailleurs. La fin d'une descente amène désormais au bilan.
 */
let bilanMontre: unknown = null

/**
 * Les paliers déjà signalés à la mesure.
 *
 * La boucle tourne quatre fois par seconde : sans mémoire, une montée de niveau
 * partirait des centaines de fois. On ne retient que le franchissement.
 */
const paliersVus = { niveau: 0, boss: -1, morts: -1 }

function boucle() {
  // Partie tenue par un autre appareil : on ne simule plus rien. `lastTick`
  // reste où il est, si bien qu'en reprenant la main c'est bien la branche de
  // l'autre appareil — la plus avancée — qui l'emportera.
  //
  // La boucle continue de tourner : elle ne simule plus, mais elle doit rester
  // vivante pour redessiner la bannière et repartir dès qu'on reprend la main.
  // S'arrêter tout court la tuait définitivement.
  if (verrou) { rendre(); requestAnimationFrame(boucle); return }
  const avant = etat.derniereDescente
  etat = simuler(etat, Date.now(), contenu)
  if (etat.derniereDescente && etat.derniereDescente !== avant && etat.derniereDescente !== bilanMontre) {
    bilanMontre = etat.derniereDescente
    // **On ne vole plus l'écran.** Une descente qui se termine imposait
    // l'onglet Monde et remontait en haut : en faisant tourner plusieurs
    // personnages sur des donjons, on se retrouvait arraché de son atelier
    // toutes les deux minutes, sans savoir ce qu'on regardait. Le bilan attend
    // maintenant dans une bannière, comme les avis de recherche — sauf si l'on
    // est déjà dans le Monde, où l'afficher est le geste naturel.
    if (onglet === "monde") {
      sous.monde = "liste"
      structure = ""
      scrollTo({ top: 0 })
    }
    const d = etat.derniereDescente
    mesurer("donjon terminé", {
      donjon: contenu.donjons[String(d.donjon)]?.nom,
      reussi: d.reussi, salle: d.salle, salles: d.salles,
      dureeS: Math.round(d.dureeS), xp: d.xp,
      butin: Object.values(d.butin).reduce((a, n) => a + n, 0),
    })
  }
  suivreProgression()
  // Le journal est consigné à **chaque pas**, hors du rendu : celui-ci est bridé
  // à `PERIODE_MS`, et le butin d'un pas non rendu se perdait, car `dernierButin`
  // ne vaut que pour le pas qui vient de s'écouler.
  consignerEquipe(etat, contenu)
  const now = Date.now()
  if (now - dernierLocal >= PERIODE_LOCAL_MS) { dernierLocal = now; ecrireLocal(etat, version) }
  rendre()
  requestAnimationFrame(boucle)
}

let syncEnCours = false
/**
 * Qui tient la partie, quand ce n'est pas nous (GDD §23).
 *
 * Tant que c'est renseigné, la simulation est **à l'arrêt** ici : continuer à
 * jouer une branche qui sera jetée est la seule perte encore évitable.
 */
let verrou: { par: string | null; depuis: number } | null = null
async function synchroniser() {
  if (syncEnCours) return
  syncEnCours = true
  try {
    const r = await ecrireDistant(ID, etat, version)
    if (r.ok) { version = r.version; statut = "synchronisé"; verrou = null }
    else if ("verrou" in r) {
      // Un autre onglet tient le bail (GDD §23). On s'arrête ici plutôt que de
      // continuer à jouer une branche qui sera jetée : c'est le seul moment où
      // la perte est encore évitable.
      verrou = r.verrou
      statut = "partie active ailleurs"
      structure = ""
      return
    }
    else { etat = arbitrer(etat, migrer(r.conflit.etat, Date.now(), etat.seed)); version = r.conflit.version; statut = "conflit arbitré" }
    ecrireLocal(etat, version)
  } catch {
    statut = "hors ligne"
  } finally {
    syncEnCours = false
  }
}

// ---------------------------------------------------------------- actions

const majPerso = (f: (p: Personnage) => Personnage) => {
  const p = perso(); if (!p) return
  etat = surPerso(etat, p.id, f)
}

/** La même chose sur **un personnage nommé** : l'écran Jeu les pilote tous. */
const surPerso = (e: Etat, id: string, f: (p: Personnage) => Personnage): Etat =>
  ({ ...e, personnages: e.personnages.map((x) => (x.id === id ? f(x) : x)) })

function creer(classeId: number) {
  const p: Personnage = {
    id: uuid(), nom: classe(classeId).nom, classe: classeId, niveau: 1, xp: 0,
    carac: { vitalite: 0, force: 0, intelligence: 0, chance: 0, agilite: 0, sagesse: 0 },
    metiers: {}, equipement: {}, pv: 0, activite: null, arrete: false,
    cible: null, tempsSurCible: 0, pointsLibres: 0, kills: 0,
  }
  etat = { ...etat, personnages: [{ ...p, pv: statsPerso(p).pvMax }], persoActif: p.id }
  void synchroniser()
}

// ---------------------------------------------------------------- rendu

let structure = ""
let dernierRendu = 0

function contexte(): Ctx {
  const p = perso()
  return {
    etat, contenu, p,
    // **Avec les effets de compte.** La fiche appelait `statsPerso(p, contenu)`
    // sans le troisième argument, quand la simulation, elle, passe
    // `effetsDofus(etat)` : le +100 Prospection du Dofus Turquoise agissait donc
    // en jeu sans jamais apparaître sur la fiche. Vyshu l'a signalé, et on lui a
    // répondu que c'était normal parce que « c'est global au compte » — ça l'est,
    // et c'est justement pour ça que ça doit se lire là où l'on regarde ses
    // caractéristiques.
    s: p ? statsPerso(p, contenu, effetsDofus(etat)) : null,
    sous: sous[onglet], sousZone, tri, filtre, filtreCraft, selection, recherche, pieceChoisie, dofusChoisi, forgeUid, forgeIssues, marche, sousMarche, filtreEtal, triEtal, nivEtal, annonceOuverte, butinOuvert, filtreDonjon, sertAOuvert, sousSocial, social, partage, chiffres, lotPieces, lotRessources, enLot, qteVente, pas, slotCraft, caracCraft, nivCraft, affiner, recetteOuverte, monstreChoisi, sourcesOuvertes, panoOuverte, paliersOuverts, rechercheSucces, filtreSucces, categorieSucces, classement,
  }
}

/**
 * Rafraîchit le sac **sans le reconstruire**.
 *
 * L'inventaire ne figure plus du tout dans la clé de structure. Il y avait
 * d'abord les types d'objets possédés, ce qui laissait les compteurs figés ;
 * puis la somme des quantités, ce qui reconstruisait l'écran plusieurs fois par
 * seconde pendant le farm — impossible d'y sélectionner un mot, la liste étant
 * remplacée sous les doigts.
 *
 * Tout ce qui bouge est donc réécrit sur place : les quantités, et l'état
 * réalisable de chaque recette.
 */
/** Le compte à rebours des vivres, réécrit en place à chaque image. */
function majRepas(p: Personnage) {
  const e = document.querySelector<HTMLElement>("#v-repas")
  if (!e) return
  const attente = Math.max(0, p.repasDans ?? 0)
  const texte = attente > 0
    ? `prêt dans ${Math.ceil(attente / 1000)} s`
    : `${nb(Object.entries(etat.inventaire).reduce((a, [id, n]) =>
        a + (estConsommable(item(contenu, Number(id))!) ? n : 0), 0))} en réserve`
  if (e.textContent !== texte) e.textContent = texte
  for (const b of document.querySelectorAll<HTMLButtonElement>("[data-manger]")) {
    const pleine = p.pv >= statsPerso(p, contenu, effetsDofus(etat)).pvMax
    b.disabled = attente > 0 || pleine
  }
}

function majSac() {
  // Les quantités de l'écran Ressources, réécrites en place.
  //
  // Vendre une partie d'un lot ne change pas la **forme** de l'écran — le même
  // objet est toujours là — donc rien ne le reconstruit, et le compteur restait
  // figé jusqu'à ce qu'on touche autre chose. Vendre le lot entier, lui, fait
  // disparaître la ligne et déclenche bien une reconstruction.
  for (const e of document.querySelectorAll<HTMLElement>("[data-qteres]")) {
    ecrire(`[data-qteres="${e.dataset.qteres}"]`, nb(etat.inventaire[Number(e.dataset.qteres)] ?? 0))
  }
  for (const e of document.querySelectorAll<HTMLElement>("[data-stockres]")) {
    const id = Number(e.dataset.stockres)
    const it = item(contenu, id)
    const reste = etat.inventaire[id] ?? 0
    ecrire(`[data-stockres="${id}"]`, nb(reste))
    if (it) ecrire(`[data-lotres="${id}"]`, `${nb(prixVente(it) * reste)} kamas`)
    // Le raccourci « Tout » vaut ce qu'il reste, sans quoi il propose de vendre
    // un stock qui n'existe plus.
    const tout = document.querySelector<HTMLElement>("#qteTout")
    if (tout) { tout.dataset.qte = String(Math.max(1, reste)); tout.textContent = "Tout" }
    const champ = document.querySelector<HTMLInputElement>("#qteVente")
    if (champ) champ.max = String(Math.max(1, reste))
  }

  for (const e of document.querySelectorAll<HTMLElement>("[data-stock]")) {
    const texte = nb(etat.inventaire[Number(e.dataset.stock)] ?? 0)
    if (e.firstChild && e.firstChild.textContent !== texte) e.firstChild.textContent = texte
  }
  for (const carte of document.querySelectorAll<HTMLElement>("[data-recette]")) {
    const r = contenu.recettes.find((x) => x.resultat === Number(carte.dataset.recette))
    if (!r) continue
    const ok = craftable(r as Recette, etat.inventaire)
    if (carte.classList.contains("ok") === ok) continue
    carte.classList.toggle("ok", ok)
    const zone = carte.querySelector(".action")
    if (zone) {
      zone.innerHTML = ok
        ? `<button class="mini primaire" data-craft="${r.resultat}">Crafter</button>`
        : `<span class="bloque">à compléter</span>`
    }
  }
}

function cleStructure(c: Ctx): string {
  if (recrutement) return "recrutement"
  if (!c.p) return "creation"
  // La forge réécrit valeurs, prix et chances à chaque tentative : sa clé porte
  // le jet lui-même, sinon un lot ne changerait rien à l'écran.
  if (forgeUid) {
    const ou = trouverPorte(c.etat, forgeUid)
    return `forge|${forgeUid}|${JSON.stringify(ou?.porte.jet ?? {})}|${c.etat.kamas}|${forgeIssues.length}`
  }
  const p = c.p
  const actDe = (x: Personnage) => !x.activite ? "0"
    : x.activite.type === "combat" ? `combat|${x.activite.familleId}`
    : x.activite.type === "recolte" ? `recolte|${x.activite.ressourceId}`
    // La salle fait partie de la structure : franchir une porte redessine
    // l'écran, ce qui remet à jour les pions et le compte de gardes.
    : `donjon|${x.activite.donjonId}|${x.descente?.salle ?? 0}`
  const base = `${onglet}|${sous[onglet]}|${p.id}|${actDe(p)}|${p.arrete}`
  // La cible ne figure **pas** dans la clé : elle change à chaque victoire, et
  // reconstruire l'écran détruisait le journal, dont le défilement repartait
  // alors en haut à chaque monstre tué. Le nom et le sprite de l'adversaire sont
  // réécrits en place par `animerCombat`.
  // Le chat se redessine sur ce qui a changé : le nombre de messages, l'erreur
  // affichée, le message visé par un signalement.
  if (onglet === "chat") {
    return `chat|${sousSocial}|${lus().length}|${lus().at(-1)?.id ?? 0}|${chat.erreur ?? ""}|${chat.vise ?? ""}|${pseudoLocal() ?? ""}` +
      `|${social.news?.length ?? -1}|${social.retours?.length ?? -1}|${social.type}|${social.envoi}|${social.erreur ?? ""}|${social.merci}` +
      // Le volet de réponse change la forme de la liste : sans lui dans la clé,
      // « Répondre » n'ouvrirait rien. Les états et réponses aussi, sinon le
      // verdict d'un retour traité n'apparaîtrait qu'au prochain aller-retour.
      `|${social.admin}|${social.repond ?? 0}|${(social.retours ?? []).map((r) => `${r.etat}:${r.reponse ? 1 : 0}`).join(",")}`
  }
  // L'écran Jeu montre **toute l'équipe** : l'activité de chacun fait donc
  // partie de sa structure. Avec la seule activité de l'actif, arrêter un
  // coéquipier ne redessinait rien et son arène continuait de le montrer au
  // combat.
  if (onglet === "jeu") {
    // La carte de partage en pied d'écran suit son propre état : le verdict de
    // copie, et l'horodatage de l'image — c'est lui qui casse le cache du
    // navigateur quand la carte vient d'être refaite.
    const carte = `${pseudoLocal() ?? ""}|${localStorage.getItem(CLE_CARTE) ?? ""}|${partage.envoi}|${partage.message ?? ""}`
    // `etat.dofus.length` : un Dofus obtenu change les dégâts affichés dans
    // l'arène sans rien changer d'autre à la forme de l'écran.
    return `${base}|${c.etat.personnages.map((x) => `${x.id}:${actDe(x)}:${x.arrete}`).join(";")}|${etat.dofus.length}|${carte}`
  }
  // Le coffre de l'écran d'équipement est le panneau du sac : ses contrôles
  // doivent donc compter dans la clé, sinon un changement de tri ne redessine rien.
  // `etat.dofus.length` : un Dofus obtenu change les caractéristiques affichées
  // — Prospection, dégâts, régénération — sans rien changer d'autre à la forme
  // de l'écran. Sans lui, la fiche gardait ses anciens chiffres jusqu'au
  // prochain changement d'équipement.
  if (onglet === "perso") return `${base}|${etat.dofus.length}|${panoOuverte}|${paliersOuverts}|${partage.envoi}|${partage.message ?? ""}|${p.niveau}|${pas}|${p.pointsLibres > 0}|${Object.values(p.equipement).map((x) => x?.uid ?? "").join(",")}|${etat.coffre.length}|${etat.personnages.length}|${pieceChoisie}|${dofusChoisi}|${tri}|${filtre}`
  if (onglet === "monde") return `${base}|${sousZone}|${p.niveau}|${etat.derniereDescente ? 1 : 0}|${recherche.trim() === "" ? "grille" : "recherche"}|${butinOuvert}|${filtreDonjon}`
  // Le **jeu des types** possédés, pas la somme des quantités : un type
  // apparaît ou disparaît rarement — un lot vendu, une ressource inédite — et
  // c'est le seul cas où la grille change de forme. Y mettre la somme
  // reconstruisait l'écran à chaque butin, et il devenait impossible d'y
  // sélectionner un mot. Les quantités, elles, sont réécrites en place.
  if (onglet === "sac" && sous.sac === "marche") {
    return `marche|${sousMarche}|${marche.annonces?.length ?? -1}|${marche.erreur ?? ""}|${
      marche.vend ? `${marche.vend.item}:${marche.vend.uid ?? ""}` : ""}|${etat.kamas}|${recherche}|${
      marche.enveloppe?.reste ?? -1}|${filtreEtal}|${triEtal}|${nivEtal.join("-")}|${annonceOuverte}|${panoOuverte}|${paliersOuverts}`
  }
  if (onglet === "sac") return `${base}|${tri}|${filtre}|${filtreCraft}|${affiner}|${nivCraft.join("-")}|${selection}|${pieceChoisie}|${etat.epingles.join(",")}|${etat.coffre.length}|${etat.enAttente?.uid ?? ""}|${Object.keys(etat.inventaire).length}|${enLot}|${lotPieces.size}|${lotRessources.size}|${panoOuverte}|${paliersOuverts}|${sertAOuvert}`
  // Le classement vient du réseau : sa clé porte donc sur ce qui est chargé,
  // sans quoi les données arrivées après coup ne s'afficheraient jamais.
  const cl = `${classement.tri}|${classement.ouvert ?? ""}|${classement.joueurs?.length ?? -1}|${classement.profil ? 1 : 0}|${classement.erreur ?? ""}`
  return `${base}|${etat.dofus.length}|${etat.archimonstres.length}|${etat.recherches.length}|${Object.keys(etat.recordsCraft).length}|${etat.almanaxJour}|${monstreChoisi}|${filtreSucces}|${categorieSucces}|${rechercheSucces}|${Object.keys(etat.succes ?? {}).length}|${cl}`
}

/**
 * Ce qu'on était en train de taper doit survivre à une reconstruction.
 *
 * L'écran se rebâtit dès que sa **forme** change — un niveau gagné, une pièce
 * tombée au coffre, un personnage qui tombe. Quand cela arrivait pendant qu'on
 * remplissait le formulaire de sécurisation, les champs repartaient vides :
 * autant dire qu'on ne pouvait pas sécuriser sa partie en farmant, ce qui est
 * précisément le moment où l'on y pense.
 *
 * Plutôt que de traquer chaque cause de reconstruction, on relève la saisie
 * avant et on la repose après, curseur compris.
 */
interface Saisie { valeurs: Map<string, string>; focus: string; debut: number | null; fin: number | null }

/**
 * Quand le jeu change lui-même une saisie, il ne faut pas la restaurer.
 *
 * Cliquer « Ébonite › » depuis une recette pose `recherche = "Ébonite"` et
 * redessine — mais `reposerSaisies` remettait aussitôt l'ancien texte tapé
 * par-dessus, et la liste ne bougeait pas. Le drapeau dit à ce rendu-là de
 * laisser les champs tels que l'écran vient de les écrire.
 */
let saisieImposee = false

function releverSaisies(): Saisie {
  const valeurs = new Map<string, string>()
  for (const e of document.querySelectorAll("#app input, #app textarea")) {
    const champ = e as HTMLInputElement
    const cle = champ.id || champ.name
    if (cle && champ.value) valeurs.set(cle, champ.value)
  }
  const a = document.activeElement as HTMLInputElement | null
  const actif = a && (a.tagName === "INPUT" || a.tagName === "TEXTAREA") ? a : null
  // `selectionStart` lève sur certains types de champ (nombre, courriel) :
  // le curseur est alors simplement replacé en fin de texte.
  let debut: number | null = null, fin: number | null = null
  try { debut = actif?.selectionStart ?? null; fin = actif?.selectionEnd ?? null } catch { /* type sans curseur */ }
  return { valeurs, focus: actif ? (actif.id || actif.name) : "", debut, fin }
}

function reposerSaisies(s: Saisie) {
  if (s.valeurs.size === 0 && !s.focus) return
  for (const e of document.querySelectorAll("#app input, #app textarea")) {
    const champ = e as HTMLInputElement
    const cle = champ.id || champ.name
    const v = cle ? s.valeurs.get(cle) : undefined
    if (v !== undefined && champ.value !== v) champ.value = v
  }
  if (!s.focus) return
  const cible = document.querySelector(`#app #${CSS.escape(s.focus)}, #app [name="${CSS.escape(s.focus)}"]`)
  const champ = cible as HTMLInputElement | null
  if (!champ) return
  champ.focus()
  try { if (s.debut !== null) champ.setSelectionRange(s.debut, s.fin ?? s.debut) } catch { /* type sans curseur */ }
}

function rendre(force = false) {
  const now = Date.now()
  if (!force && now - dernierRendu < PERIODE_MS) return
  dernierRendu = now

  const c = contexte()
  const cle = cleStructure(c)
  if (cle !== structure) {
    structure = cle
    const saisie = saisieImposee ? null : releverSaisies()
    // Sur grand écran, l'activité reste visible pendant qu'on fouille les autres
    // écrans : c'est ce qu'on regarde. La feuille de style la masque en dessous
    // de la largeur où elle tiendrait à l'étroit.
    const apercu = c.p && onglet !== "jeu" && !recrutement ? apercuActivite(c) : ""
    $("#app").innerHTML = apercu + (recrutement ? vueCreation(true)
      : !c.p ? vueCreation()
      // La forge passe devant l'écran qui l'a ouverte : on y vient d'une pièce
      // du coffre comme d'une pièce portée, et on y revient en fermant.
      : forgeUid ? vueForge(c)
      : onglet === "jeu" ? vueJeu(c)
      : onglet === "perso" ? vuePerso(c)
      : onglet === "monde" ? vueMonde(c)
      : onglet === "sac" ? vueSac(c)
      : onglet === "chat" ? `${barreSocial(c, newsNonLues(social.news ?? []))}${
          sousSocial === "news" ? vueNews(c)
          : sousSocial === "retours" ? vueRetours(c)
          : vueChat(lus(), chat)}`
      : vueCollection(c))
    if (saisie) reposerSaisies(saisie)
    saisieImposee = false
    $("#app").classList.toggle("avecApercu", Boolean(apercu))
    $("#barre").hidden = !c.p
    document.querySelectorAll<HTMLButtonElement>("#barre button")
      .forEach((b) => b.setAttribute("aria-current", String(b.dataset.onglet === onglet)))
    majFond(c)
  }

  rendrePanneau()
  majJournal()
  majBannieres()
  // « synchronisé » est le cas normal : l'afficher en permanence n'informe
  // personne et occupe la place. Seuls les états à signaler sont écrits.
  $("#statut").textContent = statut === "synchronisé" ? "" : statut
  ecrire("#v-kamas span", nb(etat.kamas))
  if (c.p) majValeurs(c)
}

/** Le décor de la page suit l'activité en cours. */
function majFond(c: Ctx) {
  const p = c.p
  let t = "plaine"
  if (p?.activite?.type === "recolte") {
    const r = recolte(contenu, p.activite.ressourceId)
    if (r) t = terrainMetier(r.metier)
  } else if (p?.activite?.type === "combat") {
    const f = famille(contenu, p.activite.familleId)
    const z = f?.zone != null ? contenu.zones[String(f.zone)] : null
    if (z) t = terrainDe(z.nom)
  }
  document.body.style.setProperty("--terrain", `url(/img/terrain/${t}.webp)`)
}

let versionAffichee = -1
function majJournal() {
  const e = document.querySelector("#journal")
  if (!e || versionJournal() === versionAffichee) return
  versionAffichee = versionJournal()
  // Remplacer le contenu remet le défilement à zéro : dès qu'on descendait lire
  // l'historique, la victoire suivante renvoyait en haut, ce qui rendait le
  // journal illisible au-delà des trois dernières lignes. On restitue donc la
  // position, décalée de ce que la nouvelle ligne a ajouté au-dessus.
  const avant = e.scrollTop
  const hauteurAvant = e.scrollHeight
  // **Le même journal que celui qu'on a dessiné.** L'écran Jeu montre l'équipe
  // entière ; réécrire ici la version d'un seul personnage aurait fait perdre
  // les autres à la première victoire, sans qu'on comprenne pourquoi.
  e.innerHTML = onglet === "jeu" && etat.personnages.length > 1
    ? rendreJournalEquipe(etat.personnages)
    : rendreJournal()
  if (avant > 0) e.scrollTop = avant + (e.scrollHeight - hauteurAvant)
}

/**
 * Peint les icônes déclarées en HTML par `data-ic`. Elles ne peuvent pas être
 * écrites dans `index.html` : leur découpe dépend de `atlas.json`, chargé à
 * l'exécution. Appelé une fois, juste après l'atlas.
 */
// Exposée pour les tests : ils vérifient qu'une icône de chaque feuille d'atlas
// pointe vers le bon fichier, ce que le DOM seul ne permet pas de contrôler.
;(window as unknown as { __icone: typeof icone; __fondMonstre: typeof fondMonstre }).__icone = icone
;(window as unknown as { __fondMonstre: typeof fondMonstre }).__fondMonstre = fondMonstre

function peindreIcones() {
  for (const e of document.querySelectorAll<HTMLElement>("[data-ic]")) {
    const st = icone("ui", e.dataset.ic!, e.classList.contains("micro") ? 16 : 22)
    if (st) e.setAttribute("style", st)
  }
}

const ecrire = (sel: string, texte: string) => {
  const e = document.querySelector(sel)
  if (e && e.textContent !== texte) e.textContent = texte
}

function majValeurs(c: Ctx) {
  const { p, s } = c
  if (!p || !s) return
  ecrire("#v-kills", nb(p.kills))
  // L'aperçu de grand écran vit hors des écrans : il a ses propres compteurs, et
  // rien ne les réécrivait. On voyait le personnage frapper pendant que son
  // total de victoires restait figé — il fallait changer d'onglet pour
  // apprendre qu'il avait gagné.
  ecrire("#v-killsapercu", nb(p.kills))
  const vApercu = p.activite?.type === "combat" ? famille(contenu, p.activite.familleId) : null
  if (vApercu) {
    const verdict = verdictFamille(s, contenu, vApercu.id, p.pv, p.niveau)
    ecrire("#v-dps", verdict ? nb(verdict.dpsInfliges) : "—")
  }
  ecrire("#v-xp", `${nb(Math.floor(p.xp))} / ${nb(xpRequise(p.niveau))}`)
  majJauge("#j-xp", p.xp / xpRequise(p.niveau), orJauge)

  // Les quantités se réécrivent partout où elles s'affichent : le sac, mais
  // aussi le bandeau de vivres de l'écran d'activité, où le stock baisse à
  // chaque repas — pris à la main ou par le personnage lui-même.
  majSac()
  majRepas(p)
  if (onglet === "perso") {
    ecrire("#v-pv", `${Math.round(p.pv)} / ${Math.round(s.pvMax)}`)
    majJauge("#j-pv", p.pv / s.pvMax, p.pv / s.pvMax > 0.35 ? vert : rougeJauge)
    ecrire("#v-points", p.pointsLibres > 0 ? `${p.pointsLibres} points` : "")
    // Le grand chiffre est le **total**, équipement compris ; la ligne de détail
    // en dessous porte la base. Écrire la base ici écrasait le total.
    const bq = bonusEquipement(p.equipement, contenu)
    for (const k of ["vitalite", "force", "intelligence", "chance", "agilite", "sagesse"] as const) {
      ecrire(`[data-v="${k}"]`, nb(p.carac[k] + bq[k]))
      // Le détail « 14 + 58 = » se met à jour avec le total : sans cela, un point
      // dépensé changeait le grand chiffre et laissait le calcul mentir.
      ecrire(`[data-calc="${k}"]`, bq[k] !== 0 ? `${nb(p.carac[k])} + ${nb(bq[k])} =` : "")
    }
    // Les pas de répartition se désactivent dès que la réserve n'y suffit plus,
    // et la remise à zéro dès qu'il n'y a plus rien à rendre.
    document.querySelectorAll<HTMLButtonElement>("[data-carac],[data-pas]").forEach((b) => {
      const v = b.dataset.combien ?? b.dataset.pas ?? "1"
      b.disabled = p.pointsLibres < (v === "tout" ? 1 : Number(v))
    })
    const raz = document.querySelector<HTMLButtonElement>("#resetCarac")
    if (raz) raz.disabled = Object.values(p.carac).every((v) => v === 0)
  }

  // Les coéquipiers de l'aperçu : ils combattent aussi, leur vie doit descendre
  // sous les yeux du joueur même quand il regarde ailleurs.
  for (const e of document.querySelectorAll<HTMLElement>("[data-pvco]")) {
    const co = etat.personnages.find((x) => x.id === e.dataset.pvco)
    if (!co) continue
    const part = Math.max(0, Math.min(1, co.pv / statsPerso(co, contenu, effetsDofus(etat)).pvMax))
    ecrire(`[data-pvco="${co.id}"]`, nb(Math.max(0, Math.round(co.pv))))
    majJauge(`#co-${co.id}`, part, part < .35 ? rougeJauge : vert)
    e.closest(".coequipier")?.classList.toggle("ko", co.arrete)
  }

  // Toute l'équipe s'anime, pas seulement le personnage affiché : l'écran
  // d'activité montre maintenant tous les personnages côte à côte, et l'aperçu
  // de grand écran réutilise les mêmes identifiants quel que soit l'onglet.
  // **Le niveau se réécrit en place, pour toute l'équipe.** Il ne figure pas
  // dans la clé de structure de l'écran Jeu — et il n'a rien à y faire, une
  // montée de niveau n'y change aucune forme — mais rien ne le réécrivait non
  // plus : il restait figé jusqu'à ce qu'autre chose redessine. « Je suis resté
  // sur la page pendant 2 h, j'ai dû rafraîchir pour actualiser mon niveau »
  // (Vyshu, 29/08/2026). L'écran Perso avait le niveau dans sa clé et se tenait
  // à jour : le même chiffre disait deux choses différentes selon l'onglet.
  //
  // Hors de la boucle ci-dessous, qui ne traite que les personnages dont l'arène
  // est à l'écran — dans l'aperçu, les coéquipiers n'en ont pas.
  for (const x of etat.personnages) {
    ecrire(`#v-niv-${x.id}`, `niv ${x.niveau}`)
    ecrire(`[data-nivco="${x.id}"]`, `niv ${x.niveau}`)
  }

  for (const x of etat.personnages) {
    if (!document.querySelector(`#arene-${x.id}`)) continue
    const sx = x.id === p.id ? s : statsPerso(x, contenu, effetsDofus(etat))
    // **Le niveau se réécrit en place.** Il ne figure pas dans la clé de
    // structure de l'écran Jeu — et il n'a rien à y faire, une montée de niveau
    // n'y change aucune forme — mais rien ne le réécrivait non plus : il restait
    // donc figé jusqu'à ce qu'autre chose redessine. « Je suis resté sur la page
    // pendant 2 h, j'ai dû rafraîchir pour actualiser mon niveau » — Vyshu,
    // 29/08/2026. L'écran Perso, lui, avait le niveau dans sa clé et se tenait
    // à jour : le même chiffre disait deux choses différentes selon l'onglet.
    ecrire(`#v-xp-${x.id}`, `${nb(Math.floor(x.xp))} / ${nb(xpRequise(x.niveau))}`)
    majJauge(`#j-xp-${x.id}`, x.xp / xpRequise(x.niveau), orJauge)
    if (x.activite?.type === "recolte") {
      const r = recolte(contenu, x.activite.ressourceId)
      if (r) {
        // Seule la jauge se mettait à jour : les chiffres restaient figés
        // jusqu'au rechargement, alors que c'est ce qu'on lit.
        const niv = niveauMetier(x, r.metier)
        majJauge(`#j-metier-${x.id}`, xpMetier(x, r.metier) / xpRequiseMetier(niv), orJauge)
        ecrire(`#v-nivmetier-${x.id}`, String(niv))
        ecrire(`#v-xpmetier-${x.id}`, `${nb(xpMetier(x, r.metier))} / ${nb(xpRequiseMetier(niv))}`)
        ecrire(`#v-stock-${x.id}`, nb(etat.inventaire[r.item] ?? 0))
      }
      animerRecolte(x)
    } else if (x.activite?.type === "donjon") {
      animerDescente(x, sx)
    } else {
      const f = x.activite?.type === "combat" ? famille(contenu, x.activite.familleId) : null
      animerCombat(x, sx, f ? verdictFamille(sx, contenu, f.id, x.pv, x.niveau) : null)
    }
  }
}

function majJauge(sel: string, ratio: number, teinte: string) {
  const e = document.querySelector<HTMLElement>(sel)
  if (!e) return
  e.style.width = `${Math.max(0, Math.min(100, ratio * 100))}%`
  e.style.background = teinte
}

// ---------------------------------------------------------------- animation

const INTERVALLE_COUP_MS = 850
const INTERVALLE_RIPOSTE_MS = 1150

/**
 * Les animations, **par personnage**.
 *
 * L'écran d'activité montre désormais toute l'équipe à la fois : chaque arène
 * porte donc son propre suffixe d'identifiant, et chaque personnage son propre
 * minuteur. Un état de module unique aurait fait battre les six panneaux au
 * rythme d'un seul — et affiché les dégâts du premier sur tous les autres.
 */
const q = (sel: string) => document.querySelector<HTMLElement>(sel)

function volant(i: string, texte: string, gauche: number, classe = "") {
  const b = q(`#a-volants-${i}`)
  if (!b) return
  const d = document.createElement("span")
  d.className = `volant ${classe}`
  d.textContent = texte
  d.style.left = `${gauche}%`
  b.appendChild(d)
  setTimeout(() => d.remove(), 900)
}

/** Le battement propre à un personnage : victoires, coups portés, ripostes. */
type Rythme = { kills: number; coup: number; riposte: number; vaincus: number; recolte: number }
const rythmes = new Map<string, Rythme>()
const rythmeDe = (id: string): Rythme => {
  let r = rythmes.get(id)
  if (!r) rythmes.set(id, (r = { kills: -1, coup: -1, riposte: 0, vaincus: -1, recolte: 0 }))
  return r
}

/** Rejoue une animation CSS en la retirant, en forçant un reflow, puis en la remettant. */
function rejouer(sel: string, classe: string) {
  const e = q(sel)
  if (!e) return
  e.classList.remove(classe)
  void e.offsetWidth
  e.classList.add(classe)
}

function animerCombat(p: Personnage, st: ReturnType<typeof statsPerso>, v: ReturnType<typeof verdictFamille>) {
  const i = p.id
  const r = rythmeDe(i)
  if (!q(`#arene-${i}`)) { rythmes.delete(i); return }
  const now = Date.now()

  if (now >= r.riposte) {
    r.riposte = now + INTERVALLE_RIPOSTE_MS
    const part = p.pv / st.pvMax
    majJauge(`#a-pvj-${i}`, part, part > 0.35 ? vert : rougeJauge)
    ecrire(`#v-pvj-${i}`, `${nb(Math.max(0, Math.round(p.pv)))} / ${nb(Math.round(st.pvMax))}`)
    if (v && !p.arrete) {
      rejouer(`#a-joueur-${i}`, "encaisse")
      rejouer(`#a-mob-${i}`, "riposte")
      const subis = Math.round(v.degatsSubisPs * (INTERVALLE_RIPOSTE_MS / 1000))
      if (subis > 0) volant(i, `−${nb(subis)}`, 10 + Math.random() * 20, "subi")
    }
  }

  // Le rythme se lit sur **le monstre au contact**, pas sur la moyenne de la
  // famille : une moyenne appliquée à un adversaire deux fois plus coriace
  // vidait sa barre bien avant qu'il ne tombe, et rendait les chiffres faux.
  const cible = p.cible !== null ? monstre(contenu, p.cible) : null
  const pal = cible ? palierDe(cible) : null
  const duree = Math.max(200, (pal ? tempsParKill(st, pal) : (v?.tempsParKillS ?? 1)) * 1000)
  const coups = Math.max(1, Math.round(duree / INTERVALLE_COUP_MS))
  const portes = Math.min(coups, Math.floor((p.tempsSurCible / duree) * coups))
  majJauge(`#a-pvm-${i}`, 1 - portes / coups, 1 - portes / coups > 0.35 ? vert : rougeJauge)
  if (pal) {
    ecrire(`#v-pvm-${i}`, `${nb(Math.max(0, Math.round(pal.pv * (1 - portes / coups))))} / ${nb(pal.pv)}`)
  }

  const kill = r.kills >= 0 && p.kills !== r.kills
  const coup = r.coup >= 0 && portes !== r.coup && portes > 0
  r.kills = p.kills
  r.coup = portes

  // Un adversaire qui arrive ne frappe pas dans la seconde : son cycle
  // d'attaque repart de zéro, sinon il « hérite » du minuteur du précédent.
  if (kill) r.riposte = Date.now() + INTERVALLE_RIPOSTE_MS
  if ((!kill && !coup) || p.arrete) return

  rejouer(`#a-joueur-${i}`, "frappe")
  rejouer(`#a-mob-${i}`, "touche")

  // Le chiffre volant est ce qu'un coup retire vraiment : la vie du monstre
  // répartie sur le nombre de coups qu'il faut pour l'abattre. Les coups
  // s'additionnent donc jusqu'à sa vie totale, au lieu de l'afficher en entier
  // à chaque frappe.
  if (pal) volant(i, `−${nb(Math.max(1, Math.round(pal.pv / coups)))}`, 56 + Math.random() * 24)

  const m = q(`#a-mob-${i}`)
  if (kill && m && cible) {
    m.setAttribute("style", fondMonstre(cible.id))
    // On ne réécrit que le nom : `.lb` porte aussi le compteur de points de vie,
    // qu'un `textContent` sur le parent effaçait à chaque victoire.
    ecrire(`#v-nommob-${i}`, cible.nom)
  }
}

/**
 * La descente, image par image.
 *
 * Le farm affiche une barre d'adversaire **reconstituée** à partir du minuteur :
 * aucun monstre n'y a de vie propre. En donjon si, et c'est tout l'intérêt —
 * la barre du boss doit descendre pour de bon, pas mimer une descente. On lit
 * donc l'état plutôt que de l'estimer.
 */
function animerDescente(p: Personnage, st: ReturnType<typeof statsPerso>) {
  const i = p.id
  const en = p.descente
  const r = rythmeDe(i)
  if (!q(`#arene-${i}`) || !en) { rythmes.delete(i); return }
  const now = Date.now()

  const part = p.pv / st.pvMax
  majJauge(`#a-pvj-${i}`, part, part > 0.35 ? vert : rougeJauge)
  ecrire(`#v-pvj-${i}`, `${nb(Math.max(0, Math.round(p.pv)))} / ${nb(Math.round(st.pvMax))}`)

  const partMob = en.pvMonstreMax > 0 ? en.pvMonstre / en.pvMonstreMax : 0
  majJauge(`#a-pvm-${i}`, partMob, partMob > 0.3 ? vert : rougeJauge)
  ecrire(`#v-pvm-${i}`, `${nb(Math.max(0, Math.round(en.pvMonstre)))} / ${nb(Math.round(en.pvMonstreMax))}`)

  const m = en.restants[0] !== undefined ? monstre(contenu, en.restants[0]) : null
  const d = contenu.donjons[String(en.donjon)]
  const boss = d ? en.salle >= d.salles : false
  ecrire(`#v-restants-${i}`, boss
    ? "Le boss, seul."
    : `${en.restants.length} garde${en.restants.length > 1 ? "s" : ""} encore debout.`)

  // Un garde qui tombe : le suivant prend sa place, sprite et nom compris.
  if (r.vaincus >= 0 && en.vaincus !== r.vaincus && m) {
    q(`#a-mob-${i}`)?.setAttribute("style", fondMonstre(m.id))
    ecrire(`#v-nommob-${i}`, m.nom)
  }
  r.vaincus = en.vaincus

  if (now >= r.riposte) {
    r.riposte = now + INTERVALLE_RIPOSTE_MS
    rejouer(`#a-joueur-${i}`, "encaisse")
    rejouer(`#a-mob-${i}`, "riposte")
  }

  rejouer(`#a-joueur-${i}`, "frappe")
  rejouer(`#a-mob-${i}`, "touche")
}

function animerRecolte(p: Personnage) {
  const i = p.id
  const r = rythmeDe(i)
  if (!q(`#a-res-${i}`)) { rythmes.delete(i); return }
  const now = Date.now()
  if (now < r.recolte) return
  r.recolte = now + 1200
  rejouer(`#a-res-${i}`, "cueille")
  q(`#a-joueur-${i}`)?.classList.add("frappe")
  setTimeout(() => q(`#a-joueur-${i}`)?.classList.remove("frappe"), 400)
}

/**
 * Va chercher l'étal.
 *
 * Rechargé à chaque ouverture du marché et après chaque geste : une annonce
 * peut partir pendant qu'on la regarde, et découvrir un « cette annonce vient
 * de partir » au moment de payer est la pire façon de l'apprendre.
 */
async function chargerEtal() {
  marche = { ...marche, chargement: true }
  try {
    // Le serveur ne connaît pas les noms : on lui donne les identifiants qui
    // correspondent à ce qu'on cherche, puisque le contenu est ici.
    const q = recherche.trim().toLowerCase()
    const items = q.length >= 2
      ? Object.values(contenu.items).filter((it) => it.nom.toLowerCase().includes(q)).map((it) => it.id)
      : []
    const e = await etalDistant(ID, items)
    marche = { ...marche, annonces: e.annonces, enveloppe: e.enveloppe, erreur: null, chargement: false }
  } catch (e) {
    marche = { ...marche, chargement: false, erreur: (e as Error).message }
  }
  rendre(true)
}

/**
 * Encaisse ce que le marché nous doit.
 *
 * Le serveur ne peut pas écrire dans la sauvegarde : il tient un journal, on le
 * rejoue, et le curseur retenu dans l'état empêche de l'appliquer deux fois.
 */
async function encaisserMarche() {
  try {
    const lignes = await creditsDistants(ID, etat.hdvCurseur ?? 0)
    if (lignes.length === 0) return
    const avant = etat.kamas
    etat = appliquerCredits(etat, lignes, contenu)
    const gagne = etat.kamas - avant
    const recus = lignes.filter((l) => l.item).reduce((a, l) => a + l.quantite, 0)
    if (gagne > 0) toast(`Ventes encaissées : ${nb(gagne)} kamas.`)
    if (recus > 0) toast(`${nb(recus)} objet${recus > 1 ? "s" : ""} reçu${recus > 1 ? "s" : ""} de l'Hôtel des Ventes.`)
    structure = ""
    void synchroniser()
  } catch { /* hors ligne : on réessaiera au prochain passage */ }
}

/**
 * Resynchronise le champ de quantité et son bouton, **sans rien redessiner**.
 *
 * `qteVente` ne figure pas dans la clé de structure, et c'est délibéré : elle
 * change à chaque touche frappée. L'y mettre reconstruisait l'écran entre deux
 * chiffres — le champ était recréé vide et le curseur repartait au début, si
 * bien qu'on ne pouvait pas taper « 234 ». C'est le même piège que pour la
 * recherche de l'atelier, et il se paie de la même façon : on écrit en place.
 *
 * `champAussi` ne vaut que pour les gestes qui **imposent** une valeur — un
 * raccourci, une vente. Pendant la frappe, réécrire le champ replacerait le
 * curseur à la fin, ce qui est exactement ce qu'on cherche à éviter.
 */
function majQuantite(champAussi = false) {
  const it = selection !== null ? item(contenu, selection) : null
  if (!it) return
  const stock = etat.inventaire[it.id] ?? 0
  const n = Math.max(1, Math.min(stock, qteVente || 1))
  if (champAussi) {
    const champ = document.querySelector<HTMLInputElement>("#qteVente")
    if (champ) { champ.value = String(n); champ.max = String(stock) }
  }
  const bouton = document.querySelector<HTMLElement>("[data-vendreqte]")
  if (bouton) bouton.textContent = `Vendre ${nb(n)} · ${nb(prixVente(it) * n)} kamas`
}

/**
 * Tient la carte de partage à jour, sans que le joueur ait rien à faire.
 *
 * Elle ne se fabriquait qu'au clic sur « Copier le lien » : la page publique
 * d'un joueur qui n'avait jamais partagé montrait donc la bannière générique du
 * site, alors que son profil, lui, était bien là. Une carte ne devrait pas
 * dépendre d'un geste — c'est justement au moment où quelqu'un d'autre découvre
 * le lien qu'elle doit exister.
 *
 * Une fois par session, et pas plus d'une fois par heure : dessiner coûte peu,
 * mais téléverser soixante-dix kilo-octets à chaque rechargement serait grossier.
 */
const CARTE_FRAICHEUR_MS = 3_600_000

async function entretenirCarte() {
  const pseudo = pseudoLocal()
  if (!pseudo) return
  try {
    // Les chiffres se lisent **à chaque lancement**, même quand la carte est
    // encore fraîche : c'est une requête minuscule, et l'écran de jeu s'en sert
    // pour proposer un texte de partage qui dise la même chose que l'image.
    // S'en passer ferait annoncer au joueur des victoires que sa propre carte
    // ne montre pas.
    const publics = await chiffresPublics(pseudo)
    if (!publics) return
    chiffres = publics
    structure = ""
    const dernier = Number(localStorage.getItem(CLE_CARTE) ?? 0)
    if (Date.now() - dernier < CARTE_FRAICHEUR_MS) return
    const png = await dessinerCarte(publics)
    if (!png) return
    await poserCarte(ID, png)
    localStorage.setItem(CLE_CARTE, String(Date.now()))
  } catch { /* hors ligne, ou navigateur sans canvas : sans conséquence */ }
}

/**
 * Le nom de l'objet survolé, sous la bande de butin (GDD §24).
 *
 * Délégué sur le document : les bandes apparaissent et disparaissent au gré des
 * dépliages, et rattacher un écouteur à chacune obligerait à les retirer.
 */
document.addEventListener("mouseover", (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>(".butin[data-nom]")
  if (!b) return
  const legende = b.closest(".bandeButinBloc")?.querySelector(".butinNom")
  if (legende && b.dataset.nom) legende.textContent = b.dataset.nom
})
// Au clavier, c'est le focus qui remplace le survol.
document.addEventListener("focusin", (e) => {
  const b = (e.target as HTMLElement).closest<HTMLElement>(".butin[data-nom]")
  if (!b) return
  const legende = b.closest(".bandeButinBloc")?.querySelector(".butinNom")
  if (legende && b.dataset.nom) legende.textContent = b.dataset.nom
})

/**
 * Les nouveautés, chargées une fois par visite.
 *
 * Le fichier est statique et livré avec le jeu : il ne changera pas tant que la
 * page est ouverte, puisqu'un déploiement recharge tout.
 */
async function chargerNews() {
  try {
    const n = await nouveautes()
    social = { ...social, news: n }
    // Ouvrir l'écran, c'est les avoir lues : la pastille disparaît.
    marquerLues(n)
    majPastilleNews()
    structure = ""
    rendre(true)
  } catch (e) { social = { ...social, erreur: (e as Error).message }; rendre(true) }
}

/** La pastille de la barre : elle vit hors de `#app`, il faut l'écrire à part. */
function majPastilleNews() {
  const b = document.querySelector<HTMLElement>("#v-newsbadge")
  if (!b) return
  const n = newsNonLues(social.news ?? [])
  b.hidden = n === 0
  b.textContent = n > 0 ? String(n) : ""
}

async function chargerRetours() {
  try {
    const boite = await lireRetours(ID)
    social = { ...social, retours: boite.retours, admin: boite.admin }
    structure = ""
    rendre(true)
  } catch (e) { social = { ...social, erreur: (e as Error).message }; rendre(true) }
}

function majBannieres() {
  const b = $("#bannieres")
  // Le verrou passe avant tout le reste : le jeu est à l'arrêt, rien d'autre
  // n'a d'importance tant qu'on n'a pas repris la main.
  if (verrou) {
    const cle = `verrou|${verrou.par ?? ""}`
    if (b.dataset.cle === cle) return
    b.dataset.cle = cle
    b.innerHTML = `<div class="banniere verrou">
      <div class="txt">
        <b>Ta partie est ouverte ailleurs</b>
        <span>${verrou.par ? `Elle tourne sur ${esc(verrou.par)}.` : "Elle tourne sur un autre appareil."}
          Ici, le jeu est en pause : sans ça, tout ce que tu ferais serait effacé
          par l'autre appareil à la prochaine synchronisation.</span>
      </div>
      <button class="primaire mini" id="prendreRelais">Reprendre ici</button>
    </div>`
    return
  }

  const a = etat.apparition
  const r = etat.dernierRecherche
  // Rappel de sécurisation : seulement quand le joueur a de quoi perdre, et
  // renvoyé une fois écarté. Le harceler dès la première minute le pousserait à
  // fermer sans lire.
  const p = perso()
  // Uniquement sur l'écran de jeu : ailleurs, elle décalait chaque panneau vers
  // le bas et repoussait hors de vue ce qu'on venait consulter.
  // Le bilan d'une descente terminée pendant qu'on était ailleurs : il attend
  // ici plutôt que d'imposer son écran.
  const dd = etat.derniereDescente && onglet !== "monde" ? etat.derniereDescente : null
  const nu = onglet === "jeu" && !pseudoLocal()
    && !localStorage.getItem("dofusidle.rappelEcarte")
    && (p?.niveau ?? 0) >= SEUIL_RAPPEL
  const cle = `${a?.monstre ?? 0}|${r?.monstre ?? 0}|${r?.vaincu ?? ""}|${nu}|${dd ? `${dd.donjon}:${dd.reussi}:${dd.salle}` : ""}`
  if (b.dataset.cle === cle) {
    if (a) ecrire("#v-expire", `${Math.max(0, Math.ceil((a.expire - Date.now()) / 1000))} s`)
    return
  }
  b.dataset.cle = cle
  b.innerHTML = `
    ${a ? `<div class="banniere archi">
      <span class="ic" style="${icone("monstres", a.monstre, 48)}"></span>
      <div class="txt"><b>${esc(nomApparition(contenu, a))}</b><span>Archimonstre, disparaît dans <span id="v-expire">…</span></span></div>
      <button class="primaire mini" id="capturer">Capturer</button></div>` : ""}
    ${r ? `<div class="banniere ${r.vaincu ? "gagne" : "perdu"}">
      <span class="ic" style="${icone("monstres", r.monstre, 48)}"></span>
      <div class="txt"><b>${esc(monstre(contenu, r.monstre)?.nom ?? "?")}</b><span>${r.vaincu ? "Avis de recherche vaincu, il rejoint ta collection." : "Il t'a eu. Le farm s'est arrêté."}</span></div>
      <button class="mini" id="fermerRecherche">OK</button></div>` : ""}
    ${dd ? `<div class="banniere ${dd.reussi ? "gagne" : "perdu"}">
      <span class="ic" style="${icone("monstres", contenu.donjons[String(dd.donjon)]?.boss[0] ?? 0, 48)}"></span>
      <div class="txt"><b>${esc(contenu.donjons[String(dd.donjon)]?.nom ?? "Donjon")}</b><span>${
        dd.reussi ? "Descente réussie." : `Arrêté salle ${dd.salle} sur ${dd.salles}.`} Le bilan t'attend.</span></div>
      <button class="primaire mini" id="voirDescente">Voir le bilan</button>
      <button class="mini" id="fermerDescente">Plus tard</button></div>` : ""}
    ${nu ? `<div class="banniere avertit">
      <div class="txt"><b>Ta partie n'existe que dans ce navigateur</b><span>Un nettoyage et elle est perdue. Deux champs suffisent à la sécuriser.</span></div>
      <button class="primaire mini" id="allerCompte">Sécuriser</button>
      <button class="mini" id="ecarterRappel">Plus tard</button></div>` : ""}`
}

/** Niveau à partir duquel perdre sa partie coûte vraiment quelque chose. */
const SEUIL_RAPPEL = 8

function vueCreation(recrute = false): string {
  return `<section class="carte">
    <h2>${recrute ? "Recruter un personnage" : "Choisis une classe"}</h2>
    <p class="aide">Chaque classe branche le compte sur un système différent : le Xélor accélère, l'Écaflip double ses jets de craft, l'Enutrof ramasse plus.</p>
    ${recrute ? `<button id="annulerRecrutement">Annuler</button>` : ""}
    <div class="classes">${CLASSES.map((c) => `
      <button class="classe" data-classe="${c.id}">
        <span class="tete" style="${icone("classes", cleClasse(c.id), 48)}"></span>
        <span class="txt"><span class="nom">${c.nom}</span><span class="sys">${esc(c.systeme)}</span></span>
      </button>`).join("")}</div>
  </section>`
}

// ---------------------------------------------------------------- événements

document.addEventListener("click", (e) => {
  const t = (e.target as HTMLElement).closest<HTMLElement>("button[data-onglet],button[data-sous],button[data-souszone],button[data-tri],button[data-pas],button[data-slotcraft],button[data-caraccraft],[data-ouvrirecette],[data-craftlot],[data-monstre],[data-fermermob],[data-filtresucces],[data-catsucces],[data-sources],button[data-tri-classement],[data-joueur],[data-signaler],button[data-filtre],[data-craftfiltre],[data-classe],[data-perso],[data-relancer],[data-arreter],[data-choisiractivite],[data-ouvrirforge],[data-forger],[data-maudire],#fermerForge,#prendreRelais,#partager,[data-pano],[data-paliers],[data-butin],[data-lotcoffre],[data-lotres],[data-qte],[data-vendreqte],#ouvrirLot,#fermerLot,#vendreLot,#broyerLot,#vendreLotRes,button[data-soussocial],button[data-versSocial],button[data-typeretour],[data-repondre],[data-etatretour],button[data-sousmarche],button[data-filtreetal],button[data-trietal],[data-annonce],[data-vendrehdv],[data-acheterannonce],[data-retirerannonce],[data-pnj],#fermerVente,[data-carac],[data-famille],[data-recolte],[data-donjon],[data-serie],[data-craft],[data-manger],[data-epingle],[data-verscraft],[data-aller],[data-piece],[data-dofus],[data-fermer],[data-retirer],[data-equiper],[data-vendreq],[data-broyer],[data-detail],[data-vendre],[data-vendre1],#fermerDescente,#voirDescente,[data-filtredonjon],[data-serta],#resetCarac,#affiner,#videFiltres,#fermerProfil,#ouvrirChat,#fermerChat,#recruter,#annulerRecrutement,#relancer,#arreter,#garder,#ranger,#jeter,#capturer,#fermerRecherche,#offrir")
  if (!t) return
  const d = t.dataset

  // Changer d'écran remonte en haut : sans ça on arrive au milieu du précédent,
  // et les sous-onglets peuvent se retrouver sous la barre de navigation.
  const remonte = () => scrollTo({ top: 0, behavior: "instant" as ScrollBehavior })

  /** Amène un élément dans la zone visible, seulement s'il en déborde. */
  const amener = (e: Element | null) => {
    if (!e) return
    const r = e.getBoundingClientRect()
    const bas = document.querySelector("#barre")?.getBoundingClientRect().top ?? innerHeight
    if (r.top < 0) scrollBy({ top: r.top - 8, behavior: "instant" as ScrollBehavior })
    else if (r.bottom > bas) {
      scrollBy({ top: Math.min(r.bottom - bas + 8, r.top), behavior: "instant" as ScrollBehavior })
    }
  }

  if (d.onglet) {
    onglet = d.onglet as Onglet
    recherche = ""
    remonte()
    // Le sondage ne démarre qu'**après** le changement d'onglet : `sonderChat`
    // s'arrête net s'il est appelé alors qu'on regarde encore ailleurs, et la
    // première lecture était perdue.
    if (onglet === "chat") {
      marquerLu()
      chat = { ...chat, erreur: null, nonLus: 0 }
      void sonderChat().then(versLeBas)
      if (!sondeChat) sondeChat = window.setInterval(() => { void sonderChat() }, 5_000)
    } else if (sondeChat) { clearInterval(sondeChat); sondeChat = 0 }
  }
  else if (d.sous === "classement") {
    sous[onglet] = "classement"
    // On oublie le cache en entrant : c'est le geste par lequel on demande
    // « où en sont les autres », pas le moment de servir une minute de retard.
    oublierCache()
    void chargerClassement()
    return
  }
  else if (d.sous) {
    sous[onglet] = d.sous
    // La sélection portait sur l'écran qu'on quitte : la garder mènerait à
    // cocher des ressources dans une barre qui parle encore de pièces.
    enLot = false; lotPieces = new Set(); lotRessources = new Set()
    if (d.sous.startsWith("z")) sousZone = "mob"
    // L'étal vient du réseau : on l'appelle en entrant, et on encaisse au
    // passage ce que le marché nous devait.
    if (d.sous === "marche") { recherche = ""; void chargerEtal(); void encaisserMarche() }
    remonte()
  }
  else if (d.souszone) sousZone = d.souszone
  else if (d.tri) tri = d.tri as Tri
  else if (d.filtre) filtre = d.filtre
  else if (d.craftfiltre !== undefined) filtreCraft = d.craftfiltre
  // Déplier une recette ne redessine que la liste : reconstruire la carte
  // entière ferait perdre le curseur du champ de recherche.
  else if (d.sources) {
    const n = Number(d.sources)
    sourcesOuvertes = sourcesOuvertes === n ? null : n
    // La recherche du monde ne figure pas dans la clé de structure : sans cela
    // le dépliage ne redessinerait rien.
    structure = ""
    saisieImposee = false
  }
  else if (d.filtresucces) { filtreSucces = d.filtresucces as typeof filtreSucces }
  else if (d.catsucces) { categorieSucces = categorieSucces === d.catsucces ? null : d.catsucces }
  else if (d.monstre) { monstreChoisi = monstreChoisi === Number(d.monstre) ? null : Number(d.monstre); remonte() }
  else if (d.fermermob) monstreChoisi = null
  else if (d.ouvrirecette) {
    const n = Number(d.ouvrirecette)
    recetteOuverte = recetteOuverte === n ? null : n
    majAtelier(); return
  }
  else if (d.craftlot) {
    const r = contenu.recettes.find((x) => x.resultat === Number(d.craftlot))
    if (r) {
      const combien = d.combien === "max" ? LOT_MAX : Number(d.combien ?? 1)
      const av = etat
      etat = crafterLot(etat, r as Recette, contenu, combien)
      if (etat !== av) { toast(`Lot fabriqué.`); majAtelier(); void synchroniser() }
    }
    return
  }
  // Les filtres de l'atelier ne redessinent que la liste : reconstruire la
  // carte entière ferait perdre le curseur du champ de recherche.
  else if (d.slotcraft !== undefined) { slotCraft = d.slotcraft; majAtelier(); return }
  else if (d.caraccraft) {
    caracCraft = caracCraft.includes(d.caraccraft)
      ? caracCraft.filter((k) => k !== d.caraccraft)
      : [...caracCraft, d.caraccraft]
    majAtelier(); return
  }
  else if (d.triClassement) { void chargerClassement(d.triClassement); return }
  else if (d.joueur) { void chargerProfil(d.joueur); return }
  else if (d.signaler) {
    void signaler(idCompte(), Number(d.signaler))
    chat = { ...chat, vise: Number(d.signaler) }
  }
  else if (t.id === "fermerProfil") { classement = { ...classement, ouvert: null, profil: null, erreur: null } }
  else if (t.id === "ouvrirChat") {
    marquerLu()
    chat = { ...chat, ouvert: true, nonLus: 0 }
    rendrePanneau(); versLeBas()
    void sonderChat()
    return
  }
  else if (t.id === "fermerChat") { chat = { ...chat, ouvert: false }; rendrePanneau(); return }
  else if (t.id === "affiner") { affiner = !affiner }
  else if (t.id === "videFiltres") {
    caracCraft = []
    nivCraft = [0, 200]
    const min = document.querySelector("[data-nivmin]") as HTMLSelectElement | null
    const max = document.querySelector("[data-nivmax]") as HTMLSelectElement | null
    if (min) min.value = "0"
    if (max) max.value = "200"
    majAtelier(); return
  }
  else if (d.classe) {
    if (recrutement) {
      etat = acheterPersonnage(etat, Number(d.classe), classe(Number(d.classe)).nom, uuid())
      recrutement = false
      mesurer("personnage recruté", { classe: classe(Number(d.classe)).nom, equipe: etat.personnages.length })
    }
    else { creer(Number(d.classe)); mesurer("partie commencée", { classe: classe(Number(d.classe)).nom }) }
    void synchroniser()
  }
  else if (d.perso) etat = choisirPersonnage(etat, d.perso)
  else if (d.pas) pas = d.pas
  else if (d.carac) {
    const combien = d.combien === "tout" ? Infinity : Number(d.combien ?? 1)
    etat = repartir(etat, d.carac as keyof Personnage["carac"], combien)
  }
  else if (d.famille) {
    viderJournal(); majPerso((p) => ({ ...relancer(p), activite: { type: "combat", familleId: Number(d.famille) } })); onglet = "jeu"; remonte()
    const f = famille(contenu, Number(d.famille))
    mesurer("farm lancé", { famille: f?.nom, niveauFamille: f?.niveauMin, niveauJoueur: perso()?.niveau })
  }
  else if (d.recolte) {
    viderJournal(); majPerso((p) => ({ ...p, activite: { type: "recolte", ressourceId: Number(d.recolte) }, cible: null, tempsSurCible: 0, arrete: false })); onglet = "jeu"; remonte()
    const r = recolte(contenu, Number(d.recolte))
    mesurer("récolte lancée", { ressource: item(contenu, r?.item ?? 0)?.nom, metier: contenu.metiers[String(r?.metier ?? 0)]?.nom })
  }
  // Entrer dans un donjon, c'est y aller : on bascule sur l'écran d'activité,
  // comme lorsqu'on choisit une famille à farmer. La descente s'y joue.
  else if (d.serie) {
    const av = etat
    const n = Math.max(1, Number(d.combien ?? 1))
    etat = lancerDonjon(etat, Number(d.serie), contenu, n).etat
    if (etat !== av) { viderJournal(); onglet = "jeu"; remonte() }
    const dj = contenu.donjons[String(d.serie)]
    mesurer("série de donjons lancée", { donjon: dj?.nom, descentes: n, entre: etat !== av })
    void synchroniser()
  }
  else if (d.donjon) {
    const av = etat
    etat = lancerDonjon(etat, Number(d.donjon), contenu).etat
    if (etat !== av) { viderJournal(); onglet = "jeu"; remonte() }
    const dj = contenu.donjons[String(d.donjon)]
    mesurer("donjon lancé", { donjon: dj?.nom, niveau: dj?.niveauMin || dj?.niveauOptimal, entre: etat !== av, niveauJoueur: perso()?.niveau })
    void synchroniser()
  }
  // « où trouver ça » : la provenance d'un ingrédient mène à l'écran qui y répond.
  else if (d.aller) {
    const [quoi, a, b] = d.aller.split(":")
    // La recherche ne figure pas dans la clé de structure — à dessein, sinon
    // chaque frappe reconstruirait l'écran. Quand c'est le **jeu** qui la
    // change, il faut donc forcer le redessin : sans cela, cliquer « Ébonite › »
    // depuis une recette de l'atelier ne bougeait rien, puisqu'on y était déjà.
    saisieImposee = true
    structure = ""
    if (quoi === "zone") {
      onglet = "monde"; sous.monde = `z${a}`; sousZone = b ?? "mob"; recherche = ""
    } else if (quoi === "monde") {
      onglet = "monde"; sous.monde = "liste"; recherche = a ?? ""
    } else if (quoi === "donjons") {
      onglet = "monde"; sous.monde = "donjons"; recherche = ""
    } else if (quoi === "collection") {
      onglet = "collec"; sous.collec = a ?? "archi"; recherche = ""
    } else if (quoi === "atelier") {
      onglet = "sac"; sous.sac = "atelier"; recherche = a ?? ""; filtreCraft = ""
    } else if (quoi === "coffre") {
      // La Pépite et la Tourmaline se broient : le coffre est l'écran où l'on
      // choisit la pièce à briser.
      onglet = "sac"; sous.sac = "equipement"; recherche = ""
    }
    remonte()
  }
  // Depuis une panoplie : la pièce qui manque mène à sa recette, déjà cherchée.
  else if (d.verscraft) {
    saisieImposee = true
    structure = ""
    const it = item(contenu, Number(d.verscraft))
    onglet = "sac"; sous.sac = "atelier"
    recherche = it?.nom ?? ""
    filtreCraft = ""
    filtre = "tout"
    remonte()
  }
  else if (d.epingle) { etat = epingler(etat, Number(d.epingle)); void synchroniser() }
  else if (d.craft) {
    const r = contenu.recettes.find((x) => x.resultat === Number(d.craft))
    if (r) {
      const avant = etat
      etat = crafter(etat, r as Recette, contenu)
      const it = item(contenu, r.resultat)
      // Un craft qui ne se porte pas n'ouvre aucun écran : sans ce mot, rien à
      // l'écran ne bouge et l'action paraît sans effet.
      if (it && etat !== avant && !etat.enAttente) {
        toast(`${it.nom} · ${nb(etat.inventaire[it.id] ?? 1)} en réserve`)
      }
    }
  }
  else if (d.piece && enLot) {
    // En mode sélection, toucher une pièce la coche au lieu de l'ouvrir.
    if (lotPieces.has(d.piece)) lotPieces.delete(d.piece)
    else lotPieces.add(d.piece)
  }
  else if (d.detail && enLot) {
    const id = Number(d.detail)
    if (lotRessources.has(id)) lotRessources.delete(id)
    else lotRessources.add(id)
  }
  else if (d.piece) { pieceChoisie = pieceChoisie === d.piece ? null : d.piece; dofusChoisi = null }
  else if (d.dofus) {
    const n = Number(d.dofus)
    dofusChoisi = dofusChoisi === n ? null : n
    pieceChoisie = null
  }
  else if (d.retirer) { etat = deposer(etat, d.retirer as Slot); pieceChoisie = null }
  else if (d.equiper) { etat = equiper(etat, d.equiper, contenu); pieceChoisie = null; void synchroniser() }
  else if (d.vendreq) { etat = vendreEquipement(etat, d.vendreq, contenu); pieceChoisie = null }
  else if (d.manger) {
    const av = etat
    etat = manger(etat, Number(d.manger), contenu)
    if (etat === av) {
      const attente = Math.ceil((av.personnages.find((x) => x.id === av.persoActif)?.repasDans ?? 0) / 1000)
      toast(attente > 0 ? `Encore ${attente} s avant de remanger.` : "Ta vie est déjà pleine.")
    }
    else {
      // **On dit ce que le repas a rendu.** Le refus était annoncé, la réussite
      // non : or à haut niveau un Pain d'Incarnam rend douze points sur mille
      // quatre cents — neuf dixièmes de pour cent de la barre, invisible à
      // l'œil. Le joueur cliquait, rien ne bougeait à l'écran, et il en
      // concluait que les vivres ne marchaient pas. Ils marchaient.
      const apres = etat.personnages.find((x) => x.id === etat.persoActif)
      const avant = av.personnages.find((x) => x.id === av.persoActif)
      const rendu = Math.round((apres?.pv ?? 0) - (avant?.pv ?? 0))
      const it = item(contenu, Number(d.manger))
      const max = apres ? Math.round(statsPerso(apres, contenu, effetsDofus(etat)).pvMax) : 0
      toast(`${it?.nom ?? "Repas"} · +${nb(rendu)} vie — ${nb(Math.round(apres?.pv ?? 0))} / ${nb(max)}`)
      // Le bandeau ne se reconstruit pas de lui-même : sans cela, le vivre
      // épuisé restait affiché et le clic paraissait sans effet.
      structure = ""
      void synchroniser()
    }
  }
  else if (d.broyer) {
    const av = etat
    const brisee = etat.coffre.find((x) => x.uid === d.broyer)
    const itBrisee = brisee ? item(contenu, brisee.item) : null
    etat = broyer(etat, d.broyer, contenu)
    // On nomme ce qui est tombé et où c'est parti : un joueur a demandé où
    // allaient les matériaux, ce qui veut dire que « en réserve » ne suffisait
    // pas à le dire.
    if (etat !== av) {
      toast(itBrisee
        ? `${rendu(contexte(), itBrisee)} · dans ton sac, Ressources › Forge`
        : "Pièce broyée · dans ton sac, Ressources › Forge")
    }
    pieceChoisie = null
    void synchroniser()
  }
  else if (d.fermer) { selection = null; pieceChoisie = null }
  else if (d.detail) selection = selection === Number(d.detail) ? null : Number(d.detail)
  else if (d.vendre1) etat = vendre(etat, Number(d.vendre1), 1, contenu)
  else if (d.vendre) etat = vendre(etat, Number(d.vendre), Infinity, contenu)
  else if (t.id === "resetCarac") {
    if (confirm("Rendre tous les points de caractéristique ? Tu pourras les replacer comme tu veux.")) {
      etat = remiseAZeroCarac(etat)
      void synchroniser()
    }
  }
  else if (t.id === "partager") {
    const pseudo = pseudoLocal()
    if (!pseudo) return
    partage = { envoi: true, message: null, erreur: false }
    structure = ""
    rendre(true)
    void (async () => {
      const lien = lienPartage(pseudo)
      // Le lien est copié **avant** l'image : un presse-papiers ne s'écrit que
      // dans la foulée d'un clic, et attendre le dessin ferait perdre ce droit.
      let copie = false
      try { await navigator.clipboard.writeText(lien); copie = true } catch { /* refusé */ }
      try {
        // Les chiffres viennent du serveur : la page publique lit le miroir
        // bridé, et une carte qui la contredirait ne vaudrait rien.
        const rang = classement.joueurs?.findIndex((j) => j.pseudo === pseudo) ?? -1
        const publics = await chiffresPublics(pseudo)
        if (publics) chiffres = publics
        const png = await dessinerCarte({
          ...(publics ?? {
            pseudo,
            niveau: perso()?.niveau ?? 1,
            points: pointsSucces(succesObtenus(etat, contenu), contenu),
            kills: etat.personnages.reduce((a, p) => a + p.kills, 0),
            donjons: etat.bossVaincus.length,
            rang: 0,
            equipe: etat.personnages.map((p) => ({ nom: p.nom, classe: p.classe, niveau: p.niveau })),
          }),
          rang: rang >= 0 ? rang + 1 : 0,
        })
        if (png) { await poserCarte(ID, png); localStorage.setItem(CLE_CARTE, String(Date.now())) }
        partage = {
          envoi: false, erreur: false,
          message: copie ? `Lien copié : ${lien}` : `Ton lien : ${lien}`,
        }
        mesurer("profil partagé", { copie })
      } catch (e) {
        partage = { envoi: false, erreur: true, message: (e as Error).message }
      }
      structure = ""
      rendre(true)
    })()
  }
  else if (t.id === "prendreRelais") {
    void (async () => {
      await prendreLeBail(ID)
      // On reprend **là où l'autre appareil en était**, pas sur notre branche
      // figée : c'est lui qui a joué depuis, et le contraire jetterait sa
      // progression au moment même où l'on cherche à la récupérer.
      try {
        const distant = await lireDistant(ID)
        if (distant) {
          etat = migrer(distant.etat, Date.now(), etat.seed)
          version = distant.version
          ecrireLocal(etat, version)
        }
      } catch { /* hors ligne : on repartira de ce qu'on a */ }
      verrou = null
      statut = "reprise ici"
      structure = ""
      viderJournal()
      toast("Partie reprise sur cet appareil.")
      void synchroniser()
    })()
  }
  else if (t.id === "recruter") recrutement = true
  else if (t.id === "annulerRecrutement") recrutement = false
  // Depuis le panneau flottant : on bascule sur l'écran entier, où le texte a
  // la place de se lire.
  else if (d.verssocial) {
    onglet = "chat"
    sousSocial = d.verssocial
    chat = { ...chat, ouvert: false }
    remonte()
    if (sousSocial === "news") void chargerNews()
    if (sousSocial === "retours") void chargerRetours()
  }
  else if (d.soussocial) {
    sousSocial = d.soussocial
    social = { ...social, merci: false, erreur: null }
    remonte()
    if (sousSocial === "news") void chargerNews()
    if (sousSocial === "retours") void chargerRetours()
  }
  else if (d.typeretour) social = { ...social, type: d.typeretour === "bug" ? "bug" : "idee", merci: false }
  else if (d.repondre) {
    const n = Number(d.repondre)
    social = { ...social, repond: n > 0 && social.repond !== n ? n : null, erreur: null }
  }
  else if (d.etatretour && d.etat) {
    const n = Number(d.etatretour)
    // Le texte se lit **dans le champ**, pas dans l'état de rendu : le volet
    // n'est reconstruit qu'à l'ouverture, donc la frappe n'y est jamais passée.
    const texte = $<HTMLTextAreaElement>("#reponseRetour")?.value ?? ""
    const etat = d.etat as "ouvert" | "vu" | "fait" | "refuse"
    void (async () => {
      try {
        await repondre(ID, n, etat, texte)
        social = { ...social, repond: null }
        await chargerRetours()
        mesurer("retour traité", { etat })
      } catch (e) {
        social = { ...social, erreur: (e as Error).message }
        structure = ""
        rendre(true)
      }
    })()
  }
  else if (d.filtredonjon !== undefined) filtreDonjon = d.filtredonjon
  else if (d.serta) {
    const n = Number(d.serta)
    sertAOuvert = sertAOuvert === n ? null : n
  }
  else if (d.butin) {
    butinOuvert = butinOuvert === d.butin ? null : d.butin
  }
  else if (d.pano) {
    const n = Number(d.pano)
    panoOuverte = panoOuverte === n ? null : n
    // Replier la panoplie replie son échelle : la rouvrir doit repartir de
    // l'état calme, sinon on retrouve le pavé qu'on venait de fermer.
    if (panoOuverte === null) paliersOuverts = null
  }
  else if (d.paliers) {
    const n = Number(d.paliers)
    paliersOuverts = paliersOuverts === n ? null : n
  }

  // ---- sélection multiple du sac
  else if (t.id === "ouvrirLot") { enLot = true; pieceChoisie = null; selection = null }
  else if (t.id === "fermerLot") { enLot = false; lotPieces = new Set(); lotRessources = new Set() }
  else if (d.lotcoffre) {
    if (d.lotcoffre === "rien") lotPieces = new Set()
    else lotPieces = new Set(piecesSelectionnables(contexte()))
  }
  else if (d.lotres) {
    if (d.lotres === "rien") lotRessources = new Set()
    else lotRessources = new Set(ressourcesVisibles(contexte()))
  }
  else if (t.id === "vendreLot") {
    const r = vendreLot(etat, [...lotPieces], contenu)
    etat = r.etat
    lotPieces = new Set()
    if (r.vendues > 0) toast(`${r.vendues} pièce${r.vendues > 1 ? "s" : ""} vendue${r.vendues > 1 ? "s" : ""} · ${nb(r.gain)} kamas`)
    mesurer("vente en lot", { pieces: r.vendues, gain: r.gain })
    void synchroniser()
  }
  else if (t.id === "broyerLot") {
    const r = broyerLot(etat, [...lotPieces], contenu)
    etat = r.etat
    lotPieces = new Set()
    if (r.broyees > 0) {
      toast(`${r.broyees} pièce${r.broyees > 1 ? "s" : ""} broyée${r.broyees > 1 ? "s" : ""} · dans ton sac, Ressources › Forge`)
    }
    mesurer("broyage en lot", { pieces: r.broyees })
    void synchroniser()
  }
  else if (t.id === "vendreLotRes") {
    const lots = [...lotRessources].map((id) => ({ item: id, qte: etat.inventaire[id] ?? 0 }))
    const r = vendreRessources(etat, lots, contenu)
    etat = r.etat
    lotRessources = new Set()
    if (r.lots > 0) toast(`${r.lots} lot${r.lots > 1 ? "s" : ""} vendu${r.lots > 1 ? "s" : ""} · ${nb(r.gain)} kamas`)
    mesurer("vente ressources en lot", { lots: r.lots, gain: r.gain })
    void synchroniser()
  }
  else if (d.qte) { qteVente = Number(d.qte); majQuantite(true) }
  else if (d.vendreqte) {
    const id = Number(d.vendreqte)
    const stock = etat.inventaire[id] ?? 0
    const n = Math.max(1, Math.min(stock, qteVente || 1))
    const avant = etat.kamas
    etat = vendre(etat, id, n, contenu)
    if (etat.kamas !== avant) toast(`${nb(n)} vendu${n > 1 ? "s" : ""} · ${nb(etat.kamas - avant)} kamas`)
    qteVente = 1
    majQuantite(true)
    void synchroniser()
  }

  // ---- Hôtel des Ventes
  else if (d.sousmarche) { sousMarche = d.sousmarche; recherche = ""; remonte(); void chargerEtal() }
  else if (d.vendrehdv) {
    marche = { ...marche, vend: { item: Number(d.vendrehdv), uid: d.uid ?? null, quantite: 1 }, erreur: null }
    sous.sac = "marche"; onglet = "sac"; remonte()
  }
  else if (t.id === "fermerVente") marche = { ...marche, vend: null, erreur: null }
  else if (d.filtreetal) { filtreEtal = d.filtreetal; annonceOuverte = null }
  else if (d.trietal) triEtal = d.trietal
  else if (d.annonce) {
    const n = Number(d.annonce)
    annonceOuverte = annonceOuverte === n ? null : n
  }
  else if (d.acheterannonce) {
    const n = Number(d.acheterannonce)
    const a = marche.annonces?.find((x) => x.id === n)
    void (async () => {
      try {
        const r = await acheterAnnonce(ID, n)
        // Les kamas partent ici, la marchandise revient par le journal : c'est
        // le serveur qui décide qu'une annonce n'est vendue qu'une fois.
        etat = { ...etat, kamas: Math.max(0, etat.kamas - r.prix) }
        toast(`Acheté pour ${nb(r.prix)} kamas.`)
        mesurer("hdv achat", { prix: r.prix, item: a?.item, quantite: a?.quantite ?? 1 })
        await encaisserMarche()
        await chargerEtal()
        void synchroniser()
      } catch (e) { marche = { ...marche, erreur: (e as Error).message }; rendre(true) }
    })()
  }
  else if (d.retirerannonce) {
    const n = Number(d.retirerannonce)
    void (async () => {
      try {
        await retirerAnnonce(ID, n)
        await encaisserMarche()
        await chargerEtal()
      } catch (e) { marche = { ...marche, erreur: (e as Error).message }; rendre(true) }
    })()
  }
  else if (d.pnj) {
    const it = item(contenu, Number(d.pnj))
    const q = Number(d.q ?? 1)
    void (async () => {
      try {
        const r = await acheterPNJ(ID, Number(d.pnj), q)
        if (etat.kamas < r.prix) {
          marche = { ...marche, erreur: "Pas assez de kamas pour ce lot." }
          return rendre(true)
        }
        etat = { ...etat, kamas: etat.kamas - r.prix }
        toast(`${q} × ${it?.nom ?? "objet"} pour ${nb(r.prix)} kamas.`)
        mesurer("hdv comptoir", { item: Number(d.pnj), quantite: q, prix: r.prix })
        await encaisserMarche()
        rendre(true)
        void synchroniser()
      } catch (e) { marche = { ...marche, erreur: (e as Error).message }; rendre(true) }
    })()
  }

  // ---- forgemagie
  else if (d.ouvrirforge) { forgeUid = d.ouvrirforge; forgeIssues = []; remonte() }
  else if (t.id === "fermerForge") { forgeUid = null; forgeIssues = [] }
  else if (d.maudire && forgeUid) {
    const r = maudireLigne(etat, forgeUid, Number(d.maudire), contenu)
    etat = r.etat
    forgeIssues = r.issues
    if (r.depense > 0) mesurer("malediction", { depense: r.depense })
    if (r.issues[0]?.type === "impossible") {
      toast(r.issues[0].raison === "kamas" ? "Pas assez de kamas." : "Cette ligne est déjà au plancher.")
    }
    void synchroniser()
  }
  else if (d.forger && forgeUid) {
    const avant = etat.kamas
    const r = forgemagie(etat, forgeUid, Number(d.forger), Number(d.lot ?? 1), contenu)
    etat = r.etat
    forgeIssues = r.issues
    const reussies = r.issues.filter((i) => i.type === "reussite").length
    if (r.depense > 0) {
      mesurer("forgemagie", {
        lot: Number(d.lot ?? 1), tentatives: r.issues.length, reussies,
        depense: r.depense, restant: etat.kamas,
      })
    }
    if (avant === etat.kamas && r.issues[0]?.type === "impossible") {
      toast(r.issues[0].raison === "kamas" ? "Pas assez de kamas." : "Cette ligne est au maximum.")
    }
    void synchroniser()
  }
  else if (t.id === "relancer") majPerso(relancer)
  else if (t.id === "arreter") { viderJournal(); majPerso((p) => ({ ...p, activite: null, cible: null, tempsSurCible: 0 })) }
  // Les boutons de l'écran Jeu portent l'identifiant du personnage : on y agit
  // sur n'importe lequel de l'équipe, sans passer par « rendre actif » d'abord.
  else if (d.relancer) etat = surPerso(etat, d.relancer, relancer)
  else if (d.arreter) {
    if (d.arreter === etat.persoActif) viderJournal()
    etat = surPerso(etat, d.arreter, (x) => ({ ...x, activite: null, cible: null, tempsSurCible: 0 }))
  }
  else if (d.choisiractivite) { etat = choisirPersonnage(etat, d.choisiractivite); onglet = "monde"; remonte() }
  else if (t.id === "garder") { etat = garderJet(etat, contenu); void synchroniser() }
  else if (t.id === "ranger") etat = rangerJet(etat)
  else if (t.id === "jeter") etat = jeterJet(etat)
  else if (t.id === "capturer") { etat = capturer(etat, Date.now()); void synchroniser() }
  else if (t.id === "fermerRecherche") etat = oublierRecherche(etat)
  else if (t.id === "offrir") { etat = offrirAlmanax(etat, contenu, Date.now()); void synchroniser() }
  else if (t.id === "fermerDescente") etat = { ...etat, derniereDescente: null }
  else if (t.id === "voirDescente") { onglet = "monde"; sous.monde = "liste"; structure = ""; scrollTo({ top: 0 }) }

  rendre(true)

  // Sélectionner un objet ouvre son détail sous la grille. On ne défile que
  // s'il déborde de la zone visible : `scrollIntoView({ block: "nearest" })`
  // tenait compte de la marge de défilement et repositionnait la page à chaque
  // changement de pièce, alors qu'on est justement en train d'en comparer deux.
  if (d.piece || d.detail || d.dofus) amener(document.querySelector(".feuille"))
})

/**
 * La recherche de recette ne repasse **pas** par le rendu complet : on remplace
 * seulement la liste. Reconstruire le panneau détruisait le champ de saisie,
 * dont le curseur repartait alors au début, et taper « piou » donnait « uoip ».
 */
/**
 * Les deux formulaires de compte.
 *
 * Ils vivent hors du rendu : une soumission attend le réseau, et le rendu, lui,
 * repasse soixante fois par seconde. On écrit donc le résultat directement dans
 * la ligne de message plutôt que d'en faire un état de plus.
 */
/**
 * Mettre en vente.
 *
 * L'ordre compte : le serveur accepte l'annonce **d'abord**, la marchandise sort
 * de la partie **ensuite**. L'inverse ferait disparaître un lot dès que le
 * réseau tousse, et l'on ne pourrait rien y faire.
 */
document.addEventListener("submit", async (e) => {
  const f = e.target as HTMLFormElement
  if (f.id === "formRetour") {
    e.preventDefault()
    const champ = f.querySelector("textarea") as HTMLTextAreaElement | null
    const texte = champ?.value.trim() ?? ""
    if (!texte) return
    social = { ...social, envoi: true, erreur: null, merci: false }
    structure = ""
    rendre(true)
    try {
      const r = await envoyerRetour(ID, social.type, texte)
      // On ajoute le retour en tête sans recharger : il vient d'arriver, le
      // serveur ne dira rien de plus, et voir sa propre ligne apparaître est ce
      // qui prouve que le clic a servi à quelque chose.
      social = {
        ...social, envoi: false, merci: true,
        retours: [r, ...(social.retours ?? [])],
      }
      mesurer("retour envoyé", { type: r.type, longueur: texte.length })
    } catch (err) {
      social = { ...social, envoi: false, erreur: (err as Error).message }
    }
    structure = ""
    rendre(true)
    return
  }
  if (f.id !== "formVente") return
  e.preventDefault()
  const v = marche.vend
  if (!v) return
  const donnees = new FormData(f)
  const it = item(contenu, v.item)
  const stock = v.uid ? 1 : (etat.inventaire[v.item] ?? 0)
  const quantite = v.uid ? 1 : Math.max(1, Math.min(stock, Math.floor(Number(donnees.get("quantite") ?? 1))))
  const prix = Math.floor(Number(donnees.get("prix") ?? 0))
  const bouton = f.querySelector("button")
  if (bouton) bouton.disabled = true

  const porte = v.uid ? etat.coffre.find((x) => x.uid === v.uid) : null
  try {
    await deposerAnnonce(ID, v.item, quantite, porte?.jet ?? null, prix)
    etat = sortirPourVente(etat, v.item, quantite, v.uid)
    marche = { ...marche, vend: null, erreur: null }
    sousMarche = "mes"
    toast(`${it?.nom ?? "Objet"} mis en vente.`)
    mesurer("hdv depot", { item: v.item, quantite, prix, piece: Boolean(v.uid) })
    void synchroniser()
    await chargerEtal()
  } catch (err) {
    if (bouton) bouton.disabled = false
    marche = { ...marche, erreur: (err as Error).message }
    rendre(true)
  }
})

document.addEventListener("submit", async (e) => {
  const f = e.target as HTMLFormElement
  if (f.id !== "formSecuriser" && f.id !== "formConnexion") return
  e.preventDefault()
  const donnees = new FormData(f)
  const pseudo = String(donnees.get("pseudo") ?? "").trim()
  const motdepasse = String(donnees.get("motdepasse") ?? "")
  const msg = document.querySelector(f.id === "formSecuriser" ? "#msgSecuriser" : "#msgConnexion")
  const bouton = f.querySelector("button")
  const dire = (texte: string, bon = false) => {
    if (msg) { msg.textContent = texte; msg.className = `msg ${bon ? "ok" : "ko"}` }
  }
  if (bouton) bouton.disabled = true
  dire("…")

  if (f.id === "formSecuriser") {
    // La partie doit exister côté serveur avant d'être rattachée : on force donc
    // une synchronisation, sinon un joueur tout neuf reçoit « aucune partie ».
    await synchroniser()
    const r = await securiser(idCompte(), pseudo, motdepasse)
    if (bouton) bouton.disabled = false
    if (!r.ok) return dire(r.erreur)
    identifier(idCompte(), pseudo)
    mesurer("partie sécurisée", { niveau: perso()?.niveau ?? 0, personnages: etat.personnages.length })
    dire("Partie sécurisée. Note bien ton pseudonyme et ton mot de passe.", true)
    structure = ""
    rendre(true)
    return
  }

  const r = await connecter(pseudo, motdepasse)
  if (bouton) bouton.disabled = false
  if (!r.ok) return dire(r.erreur)
  adopter(r.id, pseudo)
  etat = migrer(r.etat, Date.now(), (Math.random() * 2 ** 32) >>> 0)
  version = r.version
  ecrireLocal(etat, version)
  identifier(r.id, pseudo)
  mesurer("partie récupérée", { niveau: personnageActif(etat)?.niveau ?? 0 })
  dire("Partie récupérée.", true)
  // Rechargement plutôt que remise à jour : l'identité du compte a changé, et
  // tout ce qui en dépend — journal, sélections, minuteurs — doit repartir net.
  location.reload()
})

document.addEventListener("click", (e) => {
  const id = (e.target as HTMLElement).id
  if (id === "ecarterRappel") {
    localStorage.setItem("dofusidle.rappelEcarte", "1")
    $("#bannieres").dataset.cle = ""
    rendre(true)
    return
  }
  if (id === "allerCompte") {
    onglet = "perso"; sous.perso = "compte"
    structure = ""
    scrollTo({ top: 0 })
    rendre(true)
    return
  }
  if (id !== "deconnexion") return
  if (!confirm("Se déconnecter de cet appareil ? La partie reste sur le serveur, tu la retrouveras en te reconnectant.")) return
  deconnecter()
  location.reload()
})

/**
 * Rafraîchit l'atelier sans le reconstruire.
 *
 * La liste change, les puces changent d'état, mais le champ de recherche reste
 * en place : le remplacer lui ferait perdre son curseur en pleine frappe.
 */
/**
 * Charge le classement, puis redessine.
 *
 * L'écran est piloté par une clé de structure : sans remise à zéro explicite,
 * les données arrivées après coup ne s'afficheraient qu'au prochain changement
 * d'écran.
 */
/**
 * Le panneau flottant a **son propre rendu**, hors de `#app`.
 *
 * `#app` est reconstruit dès que la forme d'un écran change — une victoire, un
 * niveau, une pièce tombée au coffre. Un chat qui vivrait dedans perdrait sa
 * saisie et son défilement plusieurs fois par minute. Il a donc sa clé, et ne
 * se redessine que sur ce qui le concerne.
 */
let clePanneau = ""
function rendrePanneau() {
  const zone = document.querySelector("#chatflottant") as HTMLElement | null
  if (!zone) return
  // Sur l'écran du chat, le plein écran fait déjà le travail : deux salons
  // côte à côte n'apporteraient rien et se disputeraient le défilement.
  const montrer = onglet !== "chat" && Boolean(perso())
  zone.hidden = !montrer
  if (!montrer) { clePanneau = ""; return }

  // La pastille des nouveautés entre dans la clé : sans elle, le panneau ne se
  // redessinait pas quand elles arrivaient, et l'accès restait invisible.
  const cle = `${chat.ouvert}|${chat.nonLus}|${lus().length}|${lus().at(-1)?.id ?? 0}|${chat.erreur ?? ""}|${chat.vise ?? ""}|${pseudoLocal() ?? ""}|${newsNonLues(social.news ?? [])}`
  if (cle === clePanneau) return
  const saisie = (zone.querySelector("#formChat input") as HTMLInputElement | null)?.value ?? ""
  const enBas = estEnBas(zone)
  clePanneau = cle
  zone.innerHTML = panneauChat(lus(), chat, newsNonLues(social.news ?? []))
  const champ = zone.querySelector("#formChat input") as HTMLInputElement | null
  if (champ && saisie) champ.value = saisie
  // On ne suit le fil que si on y était déjà : sinon on arracherait la lecture
  // de quelqu'un qui est remonté dans l'historique.
  if (enBas) versLeBas()
}

const estEnBas = (zone: HTMLElement): boolean => {
  const z = zone.querySelector("#clignes")
  return !z || z.scrollHeight - z.scrollTop - z.clientHeight < 40
}

/**
 * Sonde le chat tant qu'on le regarde.
 *
 * Il n'y a pas de temps réel ici : ni WebSocket ni Durable Object, pour un
 * salon qui voit passer quelques messages par heure. Un sondage toutes les cinq
 * secondes, **et seulement quand l'onglet est ouvert**, coûte moins qu'une
 * connexion permanente et se lit pareil.
 */
async function sonderChat() {
  // On sonde tant que le chat est visible quelque part : l'onglet plein écran,
  // ou le panneau de grand écran, même replié — sinon son compteur de messages
  // manqués resterait à zéro et personne n'aurait de raison de le rouvrir.
  if (onglet !== "chat" && !panneauVisible()) return
  const r = await rafraichir()
  if (r.erreur) { chat = { ...chat, erreur: r.erreur }; rendrePanneau(); return }
  if (r.nouveaux === 0) return
  // Le chat visible est du chat lu : on ne compte que ce qui est arrivé pendant
  // qu'on regardait ailleurs. Le repère survit au rechargement, sans quoi le
  // salon entier revenait comme neuf à chaque visite.
  if (onglet === "chat" || chat.ouvert) marquerLu()
  chat = { ...chat, erreur: null, nonLus: nonLus() }
  if (onglet === "chat") { structure = ""; rendre(true); versLeBas() }
  rendrePanneau()
}

/** Le panneau n'existe qu'au-dessus de 1000 px, et hors de l'écran du chat. */
const panneauVisible = (): boolean =>
  onglet !== "chat" && window.matchMedia("(min-width: 1000px)").matches

/** Le dernier message doit être visible : c'est celui qu'on attend. */
function versLeBas() {
  for (const z of document.querySelectorAll("#clignes")) z.scrollTop = z.scrollHeight
}

async function chargerClassement(tri = classement.tri) {
  classement = { ...classement, tri, joueurs: null, erreur: null }
  structure = ""
  const r = await lireClassement(tri)
  classement = "erreur" in r
    ? { ...classement, erreur: r.erreur }
    : { ...classement, joueurs: r.joueurs, erreur: null }
  structure = ""
  rendre(true)
}

async function chargerProfil(pseudo: string) {
  classement = { ...classement, ouvert: pseudo, profil: null, erreur: null }
  structure = ""
  rendre(true)
  const r = await lireProfil(pseudo)
  classement = "erreur" in r
    ? { ...classement, erreur: r.erreur }
    : { ...classement, profil: r, erreur: null }
  structure = ""
  rendre(true)
}

function majAtelier() {
  const liste = document.querySelector("#recettes")
  if (liste) liste.innerHTML = listeRecettes(contexte())
  for (const b of document.querySelectorAll<HTMLElement>("[data-slotcraft]")) {
    b.setAttribute("aria-current", String(b.dataset.slotcraft === slotCraft))
  }
  for (const b of document.querySelectorAll<HTMLElement>("[data-caraccraft]")) {
    b.setAttribute("aria-current", String(caracCraft.includes(b.dataset.caraccraft ?? "")))
  }
  const { actifs, resume } = etatAffinage(contexte())
  const compteur = document.querySelector("#v-nbfiltres")
  if (compteur) { compteur.textContent = String(actifs); compteur.toggleAttribute("hidden", actifs === 0) }
  // Le bouton d'effacement suit le même sort : rendu une fois, montré au besoin.
  document.querySelector("#videFiltres")?.toggleAttribute("hidden", actifs === 0)
  ecrire("#v-resume", resume)
}

document.addEventListener("change", (e) => {
  const t = e.target as HTMLSelectElement

  // La tranche de niveau de l'étal, même logique de bornes que l'atelier.
  if (t.dataset.nivetalmin !== undefined || t.dataset.nivetalmax !== undefined) {
    const v = Number(t.value)
    nivEtal = t.dataset.nivetalmin !== undefined
      ? [v, Math.max(v, nivEtal[1])]
      : [Math.min(v, nivEtal[0]), v]
    annonceOuverte = null
    structure = ""
    rendre(true)
    return
  }

  if (t.dataset.nivmin === undefined && t.dataset.nivmax === undefined) return
  const v = Number(t.value)
  // Les bornes ne se croisent pas : régler le bas au-dessus du haut pousse
  // l'autre plutôt que de vider la liste sans rien dire.
  nivCraft = t.dataset.nivmin !== undefined
    ? [v, Math.max(v, nivCraft[1])]
    : [Math.min(v, nivCraft[0]), v]
  const min = document.querySelector("[data-nivmin]") as HTMLSelectElement | null
  const max = document.querySelector("[data-nivmax]") as HTMLSelectElement | null
  if (min) min.value = String(nivCraft[0])
  if (max) max.value = String(nivCraft[1])
  majAtelier()
})

document.addEventListener("submit", (e) => {
  const f = e.target as HTMLFormElement
  if (f.id !== "formChat") return
  e.preventDefault()
  const champ = f.querySelector("input[name=texte]") as HTMLInputElement | null
  const texte = champ?.value.trim() ?? ""
  if (!texte) return
  // Le champ se vide tout de suite : attendre la réponse du serveur donnerait
  // l'impression que le clic n'a rien fait.
  if (champ) champ.value = ""
  void envoyer(idCompte(), texte).then((r) => {
    chat = { ...chat, erreur: r.erreur ?? null }
    structure = ""
    rendre(true)
    // Un message refusé n'est pas perdu : on le remet dans le champ — **après**
    // le redessin, et en relisant le champ dans le DOM vivant. Écrire dans la
    // référence capturée avant l'attente visait un élément déjà remplacé.
    if (r.erreur) {
      const vivant = document.querySelector("#formChat input[name=texte]") as HTMLInputElement | null
      if (vivant && !vivant.value) vivant.value = texte
    }
    versLeBas()
  })
})

document.addEventListener("input", (e) => {
  const t = e.target as HTMLInputElement

  if (t.id === "rechercheCraft") {
    recherche = t.value
    const liste = document.querySelector("#recettes")
    if (liste) liste.innerHTML = listeRecettes(contexte())
    const q = recherche.trim()
    for (const cle of ["ok", "neuf", ""]) {
      document.querySelector(`[data-craftfiltre="${cle}"]`)
        ?.setAttribute("aria-current", String(cle === "" ? filtreCraft === "" || !!q : filtreCraft === cle && !q))
    }
    return
  }

  if (t.id === "rechercheSucces") {
    rechercheSucces = t.value
    saisieImposee = false
    structure = ""
    rendre(true)
    document.querySelector<HTMLInputElement>("#rechercheSucces")?.focus()
    return
  }

  if (t.id === "rechercheCoffre") {
    recherche = t.value
    const liste = document.querySelector("#coffre")
    if (liste) liste.innerHTML = contenuCoffre(contexte())
    return
  }

  if (t.id === "rechercheRes") {
    recherche = t.value
    const liste = document.querySelector("#ressources")
    if (liste) liste.innerHTML = contenuRessources(contexte())
    return
  }

  // Le marché cherche des deux côtés : sur l'étal, en filtrant les annonces
  // côté serveur ; au comptoir, dans tout le catalogue, côté client. On attend
  // une pause de frappe avant d'appeler le réseau — une requête par lettre
  // inonderait le Worker pour rien.
  if (t.id === "qteVente") {
    qteVente = Math.max(1, Math.floor(Number(t.value) || 1))
    majQuantite()
    return
  }

  if (t.id === "rechercheMarche") {
    recherche = t.value
    saisieImposee = false
    structure = ""
    rendre(true)
    const champ = document.querySelector<HTMLInputElement>("#rechercheMarche")
    champ?.focus()
    champ?.setSelectionRange(recherche.length, recherche.length)
    if (sousMarche === "etal") {
      clearTimeout(sondeMarche)
      sondeMarche = window.setTimeout(() => { void chargerEtal() }, 350)
    }
    return
  }

  if (t.id === "rechercheMonde") {
    const avant = recherche.trim() === ""
    recherche = t.value
    const q = recherche.trim().toLowerCase()
    const zone = document.querySelector("#resultatsMonde")
    // On passe de la grille des zones aux résultats (ou l'inverse) : là il faut
    // reconstruire. Tant qu'on reste dans les résultats, seule la liste change,
    // pour ne pas détruire le champ de saisie.
    if (avant !== (q === "") || !zone) {
      // Passage grille ↔ résultats : le champ est recréé, donc son curseur
      // repart à zéro. On le replace en fin de texte, sans quoi la frappe
      // suivante s'insère devant et le mot s'écrit à l'envers.
      structure = ""
      rendre(true)
      const champ = document.querySelector<HTMLInputElement>("#rechercheMonde")
      champ?.focus()
      champ?.setSelectionRange(recherche.length, recherche.length)
    } else {
      zone.innerHTML = resultats(contexte(), q)
    }
    return
  }
})

void demarrer()
